<template>
  <div class="view-content creator-view inventory-view">
    <div
      v-if="!allDataLoaded || isStepChangeInProgress || !sceneInitiated"
      class="loading-overlay">
      <div class="loader">
        <span>Indlæsning af data&hellip;</span>
      </div>
    </div>
    <h1
      v-if="!isSummaryVisible || (isSummaryVisible && isSummaryPopupVisible)"
      class="sr-only">
      Indret din garderobe
    </h1>

    <wardrobe-preview>
      <template v-slot:content>
        <div :class="{ 'wardrobe-views': true, 'is-disabled': isSummaryVisible }">
          <div
            ref="stage-wrapper"
            class='stage-wrapper'>
            <v-stage
              ref="stage-front"
              id="wardrobe-front"
              v-on-clickaway="away"
              class="wardrobe-stage mx-auto"
              :config="{
                height: canvasHeight,
                width: canvasWidth
              }">

              <v-layer ref="front-dimensions">
                <v-rect
                  ref="stage-background"
                  :config="{
                    height: canvasHeight,
                    width: canvasWidth,
                    x: 0,
                    y: 0,
                    fill: 'white'
                  }" />
                <v-group ref="front-dimensions-width">
                  <v-rect
                    ref="front-dimension-width-start"
                    :config="{
                      x: xOffset + wallImgWidth,
                      y: yOffset - 19,
                      width: 2,
                      height: 10,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="front-dimension-width"
                    :config="{
                      x: xOffset + wallImgWidth,
                      y: yOffset - 15,
                      width: wardrobeImgWidth,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="front-dimension-width-end"
                    :config="{
                      x: xOffset + wallImgWidth + wardrobeImgWidth - 2,
                      y: yOffset - 19,
                      width: 2,
                      height: 10,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-text
                    :config="{
                      text: wardrobeWidth + ' mm',
                      fontSize: 15,
                      fill: inventoryColors.dimensionsColor,
                      x: xOffset + wallImgWidth + wardrobeImgWidth / 2,
                      y: yOffset - 35
                    }" />
                </v-group>
                <v-group ref="front-dimensions-height">
                  <v-rect
                    ref="front-dimension-height-start"
                    :config="{
                      x: xOffset - 19,
                      y: yOffset + wallImgWidth,
                      width: 10,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="front-dimension-height"
                    :config="{
                      x: xOffset - 15,
                      y: yOffset + wallImgWidth,
                      width: 2,
                      height: wardrobeImgHeight,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="front-dimension-height-end"
                    :config="{
                      x: xOffset - 19,
                      y: yOffset + wallImgWidth + wardrobeImgHeight - 2,
                      width: 10,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-text
                    :config="{
                      text: wardrobeHeight + ' mm',
                      fontSize: 15,
                      fill: inventoryColors.dimensionsColor,
                      x: xOffset - 35,
                      y: yOffset + wallImgWidth + wardrobeImgHeight / 2,
                      rotation: -90
                    }" />
                </v-group>

                <v-group ref="front-dimensions-inventory-width">
                  <v-group
                    v-for="(dimension, dimensionIndex) in wardrobeInventoryDimensionsHoriz"
                    :key="'front-dimensions-inventory' + dimensionIndex">
                    <v-rect
                      :config="{
                        x: dimension.x,
                        y: yOffset + wardrobeImgHeight + 35,
                        width: 1,
                        height: 12,
                        fill: inventoryColors.inventoryDimensionsColor
                      }" />

                    <v-rect
                      :config="{
                        x: dimension.x,
                        y: yOffset + wardrobeImgHeight + 40,
                        width: dimension.width,
                        height: 2,
                        fill: inventoryColors.inventoryDimensionsColor
                      }" />

                    <v-rect
                      v-if="dimensionIndex === wardrobeInventoryDimensionsHoriz.length - 1"
                      ref="front-dimension-inner-width-end"
                      :config="{
                        x: dimension.x + dimension.width,
                        y: yOffset + wardrobeImgHeight + 35,
                        width: 1,
                        height: 12,
                        fill: inventoryColors.inventoryDimensionsColor
                      }" />

                    <v-text
                      :config="{
                        text: dimension.realWidth,
                        fontSize: 11,
                        fill: inventoryColors.inventoryDimensionsColor,
                        x: getHorizDimensionX(dimension, dimensionIndex),
                        y: dimensionIndex % 2 === 0 ? yOffset + wardrobeImgHeight + 50 : yOffset + wardrobeImgHeight + 20
                      }" />
                  </v-group>
                </v-group>

                <v-group ref="front-wardrobe">
                  <v-rect
                    ref="room-top"
                    class="room-top"
                    :config="{
                      x: xOffset,
                      y: yOffset,
                      width: wardrobeImgWidth + (2 * wallImgWidth),
                      height: wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />
                  <v-rect
                    v-if="isLeftWallVisible"
                    ref="room-left"
                    class="room-left"
                    :config="{
                      x: xOffset,
                      y: yOffset,
                      width: wallImgWidth,
                      height: wardrobeImgHeight + wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />
                  <v-rect
                    v-if="isRightWallVisible"
                    ref="room-right"
                    class="room-right"
                    :config="{
                      x: xOffset + wardrobeImgWidth + wallImgWidth,
                      y: yOffset,
                      width: wallImgWidth,
                      height: wardrobeImgHeight + wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />

                  <v-rect
                    v-if="hasLeftImpact || hasLeftGable"
                    ref="wardrobe-left"
                    class="wardrobe-left"
                    :config="{
                      x: xOffset + wallImgWidth,
                      y: yOffset + wallImgWidth,
                      width: wardrobeImgSideWallThickness,
                      height: wardrobeImgHeight,
                      fill: inventoryColors.gableColor
                    }" />
                  <v-rect
                    v-if="hasRightImpact || hasRightGable"
                    ref="wardrobe-right"
                    class="wardrobe-right"
                    :config="{
                      x: xOffset + wallImgWidth + wardrobeImgWidth - wardrobeImgSideWallThickness,
                      y: yOffset + wallImgWidth,
                      width: wardrobeImgSideWallThickness,
                      height: wardrobeImgHeight,
                      fill: inventoryColors.gableColor
                    }" />

                  <v-rect
                    v-if="!hasDoor"
                    ref="room-border-left"
                    class="room-left"
                    :config="{
                      x: xOffset + wallImgWidth - 3,
                      y: yOffset + wallImgWidth,
                      width: 3,
                      height: wardrobeImgHeight,
                      fill: inventoryColors.roomBorderColor
                    }" />
                  <v-rect
                    v-if="!hasDoor"
                    ref="room-border-right"
                    class="room-right"
                    :config="{
                      x: xOffset + wallImgWidth + wardrobeImgWidth,
                      y: yOffset + wallImgWidth,
                      width: 3,
                      height: wardrobeImgHeight,
                      fill: inventoryColors.roomBorderColor
                    }" />
                </v-group>
              </v-layer>

              <v-layer ref="front-inventory">
                <v-rect
                  v-if="isDragging && isInsertable"
                  ref="front-in-drag"
                  :config="highlightItemFront" />

                <v-image
                  v-for="item in frontViewVerticalItems"
                  :key="'front-view-vert-items-' + item.id"
                  :config="item"
                  :draggable="item.isWardrobeFrame || isSummaryVisible ? false : true"
                  @dragmove="handleItemDragMove"
                  @dragend="handleItemDragEnd"
                  @dragstart="handleItemDragStart"
                  @click="handleItemClick($event, item.id, item.inventoryCategoryId)">
                </v-image>

                <v-group
                  v-for="item in frontViewHorizontalItems"
                  :key="'front-view-horiz-items-' + item.id">
                  <v-image
                    v-if="!item.isGableTop"
                    :config="item"
                    :draggable="!item.isAdditionalTop"
                    @dragmove="handleItemDragMove"
                    @dragend="handleItemDragEnd"
                    @dragstart="handleItemDragStart"
                    @click="item.isAdditionalTop ? {} : handleItemClick($event, item.id)">
                  </v-image>
                </v-group>
              </v-layer>
            </v-stage>
          </div>

          <div class='stage-wrapper'>
            <v-stage
              ref="stage-top"
              id="wardrobe-top"
              class="wardrobe-stage"
              :config="configKonvaTop">

              <v-layer ref="top-dimensions">
                <v-rect
                  ref="stage-top-background"
                  :config="{
                    height: configKonvaTop.height,
                    width: configKonvaTop.width,
                    x: 0,
                    y: 0,
                    fill: 'white'
                  }" />
                <v-group ref="top-dimensions-depth">
                  <v-rect
                    ref="top-dimension-depth-start"
                    :config="{
                      x: xOffset - 19,
                      y: topViewYOffset + wallImgWidth,
                      width: 10,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="top-dimension-depth"
                    :config="{
                      x: xOffset - 15,
                      y: topViewYOffset + wallImgWidth,
                      width: 2,
                      height: wardrobeImgDepth,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="top-dimension-depth-end"
                    :config="{
                      x: xOffset - 19,
                      y: topViewYOffset + wallImgWidth + wardrobeImgDepth - 2,
                      width: 10,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-text
                    :config="{
                      text: wardrobeDepth + ' mm',
                      fontSize: 15,
                      fill: inventoryColors.dimensionsColor,
                      x: xOffset - 35,
                      y: topViewYOffset + wallImgWidth + wardrobeImgDepth / 2,
                      rotation: -90
                    }" />
                </v-group>
                <v-group ref="top-dimensions-inner-width">
                  <v-rect
                    ref="top-dimension-inner-width-start"
                    :config="{
                      x: wardrobeInnerLeftOffsetX,
                      y: topViewYOffset + wardrobeImgDepth + 30,
                      width: 2,
                      height: 10,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="top-dimension-inner-width"
                    :config="{
                      x: wardrobeInnerLeftOffsetX,
                      y: topViewYOffset + wardrobeImgDepth + 34,
                      width: wardrobeImgInnerWidth,
                      height: 2,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-rect
                    ref="top-dimension-inner-width-end"
                    :config="{
                      x: wardrobeInnerLeftOffsetX + wardrobeImgInnerWidth - 2,
                      y: topViewYOffset + wardrobeImgDepth + 30,
                      width: 2,
                      height: 10,
                      fill: inventoryColors.dimensionsColor
                    }" />

                  <v-text
                    :config="{
                      text: wardrobeInnerWidth + ' mm',
                      fontSize: 15,
                      fill: inventoryColors.dimensionsColor,
                      x: wardrobeInnerLeftOffsetX + wardrobeImgWidth / 2,
                      y: topViewYOffset + wallImgWidth + wardrobeImgDepth + 30
                    }" />
                </v-group>

                <v-group ref="top-wardrobe-room">
                  <v-rect
                    ref="rect"
                    class="room-back"
                    :config="{
                      x: xOffset,
                      y: topViewYOffset,
                      width: wardrobeImgWidth + (2 * wallImgWidth),
                      height: wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />
                  <v-rect
                    v-if="isLeftWallVisible"
                    ref="rect"
                    class="room-left"
                    :config="{
                      x: xOffset,
                      y: topViewYOffset,
                      width: wallImgWidth,
                      height: wardrobeImgDepth + wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />
                  <v-rect
                    v-if="isRightWallVisible"
                    ref="rect"
                    class="room-right"
                    :config="{
                      x: xOffset + wardrobeImgWidth + wallImgWidth,
                      y: topViewYOffset,
                      width: wallImgWidth,
                      height: wardrobeImgDepth + wallImgWidth,
                      fill: inventoryColors.wallsColor
                    }" />

                  <v-rect
                    v-if="hasLeftGable || hasLeftImpact"
                    ref="rect"
                    class="wardrobe-left"
                    :config="{
                      x: xOffset + wallImgWidth,
                      y: hasLeftImpact ? topViewYOffset + wallImgWidth + (wardrobeImgDepth - wardrobeImgImpactDepth) : topViewYOffset + wallImgWidth,
                      width: wardrobeImgSideWallThickness,
                      height: hasLeftImpact ? wardrobeImgImpactDepth : wardrobeImgDepth,
                      fill: inventoryColors.gableColor
                    }" />
                  <v-rect
                    v-if="hasRightGable || hasRightImpact"
                    ref="rect"
                    class="wardrobe-right"
                    :config="{
                      x: xOffset + wallImgWidth + wardrobeImgWidth - wardrobeImgSideWallThickness,
                      y: hasRightImpact ? topViewYOffset + wallImgWidth + (wardrobeImgDepth - wardrobeImgImpactDepth) : topViewYOffset + wallImgWidth,
                      width: wardrobeImgSideWallThickness,
                      height: hasRightImpact ?  wardrobeImgImpactDepth : wardrobeImgDepth,
                      fill: inventoryColors.gableColor
                    }" />

                  <v-rect
                    v-if="!hasDoor"
                    ref="room-border-left"
                    class="roo-topm-left"
                    :config="{
                      x: xOffset + wallImgWidth - 3,
                      y: topViewYOffset + wallImgWidth,
                      width: 3,
                      height: wardrobeImgDepth,
                      fill: inventoryColors.roomBorderColor
                    }" />
                  <v-rect
                    v-if="!hasDoor"
                    ref="room-top-border-right"
                    class="room-right"
                    :config="{
                      x: xOffset + wallImgWidth + wardrobeImgWidth,
                      y: topViewYOffset + wallImgWidth,
                      width: 3,
                      height: wardrobeImgDepth,
                      fill: inventoryColors.roomBorderColor
                    }" />
                </v-group>

                <v-group>
                  <v-rect
                    v-for="(door, index) in wardrobeImgDoors"
                    :key="'wardrobe-door-' + index"
                    :config="{
                      fill: door.fill,
                      height: door.height,
                      width: door.width,
                      x: door.x,
                      y: door.y
                    }" />
                </v-group>

                <!-- for testing purposes -->
                <!-- <v-group>
                  <v-rect
                    v-for="(door, index) in wardrobeImgDoorsConnectionPoints"
                    :key="index + '-test'"
                    :config="{
                      fill: 'red',
                      height: 5,
                      width: door.width,
                      x: door.x,
                      y: yOffset + wardrobeImgDepth + wallImgWidth + 5
                    }" />
                </v-group> -->
              </v-layer>

              <v-layer ref="top-wardrobe">
                <v-group ref="top-inventory">
                    <v-rect
                    v-for="item in topViewInventory"
                    :key="'top-view-inventory-' + item.id"
                    :config="item">
                  </v-rect>
                  <v-rect
                    v-if="isDragging && isInsertable && highlightItemTop.isVisible"
                    ref="top-in-drag"
                    :config="highlightItemTop" />
                </v-group>
              </v-layer>
            </v-stage>
          </div>
        </div>
      </template>
    </wardrobe-preview>

    <right-sidebar>
      <!-- in inventory view store is updated after every inventory change therefore there is no local store different from global  -->
      <template v-slot:header>
        <h2>Indretning</h2>
      </template>

      <template v-slot:content>
        <div
          v-if="existsItemWidthCollision"
          class="alert alert-warning alert-small px-2 mb-2">
          Der mangler oplysninger i konfigurationen, eller den skal verificeres. Klik på “Rediger skab” for at kontrollere og færdiggøre indstillingerne. Bemærk, at den endelige pris kan ændre sig efter verificering.
        </div>
        <div v-if="clickedItemId">
          <c-button
            theme="primary"
            class="mb-2 full-width"
            :is-small="true"
            @click.prevent="deselectItem">
            Fravælg
          </c-button>

          <inventory-item-thumbnail
            :inventory-item="selectedSourceItem" />
          <c-button
            theme="warning"
            class="my-2 full-width"
            :is-small="true"
            @click.prevent="removeItem"
            :disabled="selectedItem ? selectedItem.isWardrobeFrame : false">
            Fjern Element
          </c-button>
        </div>
        <inventory-list
          v-else
          :biggest-space="wardrobeRealBiggestAvailableSpaceHoriz"
          :inventoryInWardrobe="selectedInventory"
          @add-item-to-wardrobe="addInventory" />

        <c-button
          theme="warning"
          class="mb-2 full-width mt-auto"
          :disabled="!selectedInventory.length"
          :is-small="true"
          @click.prevent="clearScene">
          Fjern alt inventar
        </c-button>

        <!-- <c-button
          v-if="isDebugMode"
          theme="warning"
          class="mb-2 full-width mt-auto"
          :disabled="!selectedInventory.length"
          :is-small="true"
          @click.prevent="exportToPreset">
          Export to preset
        </c-button> -->
      </template>

      <template v-slot:footer>
        <navigation-buttons
          @nextBtnClicked="goNextStep"
          @backBtnClicked="goBack" />
      </template>
    </right-sidebar>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import CustomDecimals from '@/utils/CustomDecimals.js';
import InventoryList from '@/components/forms/inventory/InventoryList.vue';
import InventoryItemThumbnail from '@/components/layout/InventoryItemThumbnail.vue';
import { mixin as clickaway } from 'vue-clickaway';
import inventoryColors from '@/data/inventoryColors.js';
import InventoryParser from '@/utils/InvenotryToPresetParser.js';

const CATEGORY_GABLE = 2;
const CATEGORY_SHELF = 3;
const CATEGORY_FLEX_GABLE = 4;
const GABLE_DEPTH = 501;
const DEFAULT_GABLE = 780;

export default {
  name: 'wardrobe-inventory',
  components: {
    'inventory-list': InventoryList,
    'inventory-item-thumbnail': InventoryItemThumbnail
  },
  mixins: [clickaway],
  inject: ['isDebugMode'],
  computed: {
    ...mapGetters('creator', [
      'wardrobeHeight',
      'wardrobeWidth',
      'wardrobePositionId',
      'doorsNumber',
      'doorsNumberObject',
      'wardrobeGableId',
      'wardrobeImpact',
      'hasDoor',
      'hasInventoryOnly'
    ]),
    ...mapGetters('application', [
      'isSummaryPopupVisible'
    ]),
    ...mapGetters('apiData', [
      'optionsWardrobePositions',
      'optionsGable',
      'optionsInventoryCategories',
      'optionsInventory'
    ]),
    isSummaryVisible () {
      if (this.isSummaryPopupVisible) {
        return true;
      }

      if (this.$route.query && this.$route.query.wardrobeSummary) {
        return true;
      }

      return false;
    },
    currentPosition () {
      return this.optionsWardrobePositions.find(item => item.id === this.wardrobePositionId);
    },
    hasLeftImpact () {
      if (this.wardrobeImpact && this.currentPosition && this.currentPosition.impact_position && this.currentPosition.impact_position.indexOf('left') > -1) {
        return true;
      }

      return false;
    },
    hasRightImpact () {
      if (this.wardrobeImpact && this.currentPosition && this.currentPosition.impact_position && this.currentPosition.impact_position.indexOf('right') > -1) {
        return true;
      }

      return false;
    },
    hasLeftGable () {
      if (this.wardrobeGableId && this.currentPosition && this.currentPosition.has_gable && this.currentPosition.gable_position.indexOf('left') > -1) {
        return true;
      }

      return false;
    },
    hasRightGable () {
      if (this.wardrobeGableId && this.currentPosition && this.currentPosition.has_gable && this.currentPosition.gable_position.indexOf('right') > -1) {
        return true;
      }

      return false;
    },
    isLeftWallVisible () {
      if (!this.currentPosition) {
        return false;
      }

      if (this.currentPosition.impact_position && this.currentPosition.impact_position.indexOf('left') > -1) {
        return true;
      }

      return false;
    },
    isRightWallVisible () {
      if (!this.currentPosition) {
        return false;
      }

      if (this.currentPosition.impact_position && this.currentPosition.impact_position.indexOf('right') > -1) {
        return true;
      }

      return false;
    },
    wardrobeImgImpactDepth () {
      return this.getProportionalSizeValue(this.data.impactDepth);
    },
    wardrobeImgSideWallThickness () {
      return this.getProportionalSizeValue(this.data.sideWallThickness);
    },
    wardrobeDepth () {
      if (this.hasInventoryOnly) {
        return GABLE_DEPTH;
      }

      let depth = DEFAULT_GABLE;

      if (this.wardrobeGableId) {
        let currentGable = this.optionsGable.find(item => item.id === this.wardrobeGableId);

        if (currentGable && currentGable.depth) {
          depth = currentGable.depth;
        }
      }
      return depth;
    },
    wardrobeImgDepth () {
      return this.getProportionalSizeValue(this.wardrobeDepth);
    },
    wardrobeImgInnerWidth () {
      let width = this.wardrobeImgWidth;

      if (this.hasLeftGable) {
        width -= this.wardrobeImgSideWallThickness;
      }

      if (this.hasRightGable) {
        width -= this.wardrobeImgSideWallThickness;
      }

      return CustomDecimals.toNumberFormatted(width);
    },
    wardrobeInnerWidth () {
      let width = Number(this.wardrobeWidth);

      if (this.hasLeftGable) {
        width -= this.data.sideWallThickness;
      }

      if (this.hasRightGable) {
        width -= this.data.sideWallThickness;
      }

      return CustomDecimals.toNumberFormatted(width, 2);
    },
    wardrobeImgInnerAvailableWidth () {
      let width = this.wardrobeImgWidth;

      if (this.hasLeftGable) {
        width -= this.wardrobeImgSideWallThickness;
      }

      if (this.hasRightGable) {
        width -= this.wardrobeImgSideWallThickness;
      }

      width -= (2 * this.inventWallAdditionalDistFromWardrobe);

      return CustomDecimals.toNumberFormatted(width);
    },
    inventoryMinXPos () {
      let offset = this.xOffset + this.wallImgWidth;

      if (this.hasLeftGable) {
        offset += this.wardrobeImgSideWallThickness;
      }

      offset += this.inventWallAdditionalDistFromWardrobe;

      return CustomDecimals.toNumberFormatted(offset, 2);
    },
    wardrobeInnerLeftOffsetX () {
      let offset = this.xOffset + this.wallImgWidth;

      if (this.hasLeftGable) {
        offset += this.wardrobeImgSideWallThickness;
      }

      return CustomDecimals.toNumberFormatted(offset, 2);
    },
    inventoryFormatted () {
      if (!this.allDataLoaded) {
        return [];
      }
      let inventoryList = JSON.parse(JSON.stringify(this.optionsInventory));

      if (!inventoryList.length || !this.optionsInventoryCategories.length) {
        return [];
      }

      return inventoryList.map(item => {
        let itemCategory = this.optionsInventoryCategories.find(category => category.id === item.category_id);

        if (itemCategory.type === 'preset') {
          item.type = itemCategory.type;
          item.categoryID = item.category_id;
          return item;
        }

        item.depthOnImg = item.depth ? this.getProportionalSizeValue(item.depth) : 0;
        item.heightOnImg = item.height ? this.getProportionalSizeValue(item.height) : 0;
        item.widthOnImg = item.width ? this.getProportionalSizeValue(item.width) : 0;
        item.visibleFromTop = itemCategory && itemCategory.top_view;
        item.type = itemCategory.type;
        item.categoryID = item.category_id;

        item.snapToFloor = itemCategory && itemCategory.snap_floor;
        item.snapToCategory = itemCategory && itemCategory.snap_to_category ? itemCategory.snap_to_category : [];
        item.fitsIn = item.fits_in ? item.fits_in : [];
        item.isPullOut = !!item.is_pull_out;

        return item;
      });
    },
    itemOutOfBoundsBorders () {
      return {
        leftBorder: this.xOffset - 20,
        rightBorder: this.xOffset + this.wallImgWidth + this.wardrobeImgWidth + 20,
        topBorder: this.yOffset - 20,
        bottomBorder: this.yOffset + this.wallImgWidth + this.wardrobeImgHeight + 20
      }
    },
    configKonvaTop () {
      return {
        width: this.canvasWidth,
        height: this.wardrobeImgDepth + (2 * this.wallImgWidth) + (2 * this.yOffset)
      }
    },
    categoryTypesInScene () {
      return this.inventoryFormatted.filter(item => this.selectedInventory.indexOf(item.id) > -1).map(item => item.category_id)
    },
    wardrobeFloorPosY () {
      return this.yOffset + this.wallImgWidth + this.wardrobeImgHeight;
    },
    wardrobeAvailableSpaceHoriz () {
      let availableSpaceArray = [];

      if (!this.selectedInventory.length) {
        availableSpaceArray.push({
          realWidth: this.getProportionalRealSizeValue(this.wardrobeImgWidth, false, 0),
          width: CustomDecimals.toNumberFormatted(this.wardrobeImgWidth),
          x: CustomDecimals.toNumberFormatted(this.inventoryMinXPos)
        });
      } else {
        let verticalItems = this.frontViewInventory.filter(item => item.positionType === 'vertical');
        verticalItems.sort((CustomDecimals.sortAscBy('x')));
        for (let i = 0; i < verticalItems.length; i++) {
          if (i === 0 && Number(verticalItems[i].x) > this.inventoryMinXPos) {
            let width = Number(verticalItems[i].x) - Number(this.inventoryMinXPos);
            availableSpaceArray.push({
              realWidth: this.getProportionalRealSizeValue(width, false, 0),
              width: CustomDecimals.toNumberFormatted(width),
              x: CustomDecimals.toNumberFormatted(this.inventoryMinXPos)
            })
          }

          if (i === verticalItems.length - 1) {
            if ((Number(verticalItems[i].x) + Number(verticalItems[i].width)) < (this.inventoryMinXPos + this.wardrobeImgInnerWidth)) {
              let width = (Number(this.inventoryMinXPos + this.wardrobeImgInnerWidth) - this.inventWallAdditionalDistFromWardrobe - (Number(verticalItems[i].x) + Number(verticalItems[i].width)));
              availableSpaceArray.push({
                realWidth: this.getProportionalRealSizeValue(width, false, 0),
                width: CustomDecimals.toNumberFormatted(width),
                x: CustomDecimals.toNumberFormatted(Number(verticalItems[i].x) + Number(verticalItems[i].width))
              })
            }
          } else {
            let spaceHeight = Math.min(Number(verticalItems[i].height), Number(verticalItems[i + 1].height));
            let width = Number(verticalItems[i + 1].x) - (Number(verticalItems[i].x) + Number(verticalItems[i].width));
            availableSpaceArray.push({
              height: spaceHeight,
              realWidth: this.getProportionalRealSizeValue(width, false, 0),
              width: CustomDecimals.toNumberFormatted(width),
              x: CustomDecimals.toNumberFormatted(Number(verticalItems[i].x) + Number(verticalItems[i].width)),
              y: CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - spaceHeight)
            })
          }
        }
      }

      return availableSpaceArray;
    },
    potentialSpacesForInventoryHoriz () {
      if (!this.frontViewInventory.length) {
        return [];
      }
      const verticalItems = this.frontViewInventory.filter(item => item.positionType === 'vertical');
      verticalItems.sort((CustomDecimals.sortAscBy('x')));

      let output = [];

      for (let i = 0; i < verticalItems.length - 1; i++) {
        const currentItem = verticalItems[i];
        const nextItem = verticalItems[i + 1];

        let spaceWidth = CustomDecimals.toNumberFormatted(nextItem.x - (currentItem.x + currentItem.width))

        if (spaceWidth > 0) {
          let spaceHeight = Number(currentItem.height); // all vertical items have same height
          output.push({
            height: spaceHeight,
            realWidth: this.getProportionalRealSizeValue(spaceWidth, false, 0),
            width: spaceWidth,
            x: CustomDecimals.toNumberFormatted(currentItem.x + currentItem.width),
            y: CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - spaceHeight)
          });
        }
      }

      return output;
    },
    wardrobeRealBiggestAvailableSpaceHoriz () {
      if (!this.wardrobeAvailableSpaceHoriz.length) {
        return 0;
      }
      return this.getProportionalRealSizeValue(Math.max(...this.wardrobeAvailableSpaceHoriz.map(item => item.width)), true);
    },
    wardrobeInventoryDimensionsHoriz () {
      let dimensionsArray = [];
      let offsetX = this.xOffset + this.wallImgWidth;

      if (this.hasLeftGable) {
        dimensionsArray.push({
          x: offsetX,
          width: this.wardrobeImgSideWallThickness,
          realWidth: this.data.sideWallThickness
        });

        offsetX = this.wardrobeInnerLeftOffsetX; // this offset includes left gable
      }

      if (!this.selectedInventory.length) {
        dimensionsArray.push({
          x: offsetX,
          width: this.wardrobeImgInnerWidth,
          realWidth: this.wardrobeInnerWidth
        });
      } else {
        let verticalItems = this.frontViewInventory.filter(item => item.positionType === 'vertical');
        verticalItems.sort((CustomDecimals.sortAscBy('x')));
        for (let i = 0; i < verticalItems.length; i++) {
          let currentItem = verticalItems[i];
          let widthOnImg = 0;
          let inventoryItemRealWidth = CustomDecimals.toNumberFormatted(this.optionsInventory.find(item => item.id === currentItem.inventoryItemId).width);
          if (i === 0 && Number(currentItem.x) > offsetX) {
            widthOnImg = CustomDecimals.toNumberFormatted(Number(currentItem.x) - offsetX);
            dimensionsArray.push({
              x: offsetX,
              width: widthOnImg,
              realWidth: CustomDecimals.toNumberFormatted(this.getProportionalRealSizeValue(widthOnImg, true), 0)
            })
          }

          let itemX = currentItem.x;
          let itemWidth = currentItem.width;
          let itemRealWidth = inventoryItemRealWidth;

          if (currentItem.inventoryCategoryId === 2) {
            // perform for gables only
            if (currentItem.isReversed) {
              itemX = currentItem.x;
              itemWidth = currentItem.width - this.inventoryBoardThicknessOnImg;
              itemRealWidth = inventoryItemRealWidth - this.inventoryBoardThickness;
              dimensionsArray.push(this.getObjectForHorizDimensions(itemX, itemWidth, itemRealWidth));

              itemX = currentItem.x + (currentItem.width - this.inventoryBoardThicknessOnImg);
              itemWidth = this.inventoryBoardThicknessOnImg;
              itemRealWidth = this.inventoryBoardThickness;
              dimensionsArray.push(this.getObjectForHorizDimensions(itemX, itemWidth, itemRealWidth));
            } else {
              itemX = currentItem.x;
              itemWidth = this.inventoryBoardThicknessOnImg;
              itemRealWidth = this.inventoryBoardThickness;
              dimensionsArray.push(this.getObjectForHorizDimensions(itemX, itemWidth, itemRealWidth));

              itemX = currentItem.x + this.inventoryBoardThicknessOnImg;
              itemWidth = currentItem.width - this.inventoryBoardThicknessOnImg;
              itemRealWidth = inventoryItemRealWidth - this.inventoryBoardThickness;
              dimensionsArray.push(this.getObjectForHorizDimensions(itemX, itemWidth, itemRealWidth));
            }
          } else {
            dimensionsArray.push(this.getObjectForHorizDimensions(itemX, itemWidth, itemRealWidth));
          }

          if (i === verticalItems.length - 1) {
            widthOnImg = CustomDecimals.toNumberFormatted((this.wardrobeInnerLeftOffsetX + this.wardrobeImgInnerWidth) - (Number(currentItem.x) + Number(currentItem.width)), 3);
            let realWidthVal = CustomDecimals.toNumberFormatted(this.getProportionalRealSizeValue(widthOnImg), 0);
            if (widthOnImg > 0) {
              dimensionsArray.push({
                x: CustomDecimals.toNumberFormatted(Number(currentItem.x) + Number(currentItem.width)),
                width: widthOnImg,
                realWidth: realWidthVal
              })
            }
          } else {
            widthOnImg = CustomDecimals.toNumberFormatted(Number(verticalItems[i + 1].x) - (Number(currentItem.x) + Number(currentItem.width)));
            if (widthOnImg > 0) {
              dimensionsArray.push({
                x: CustomDecimals.toNumberFormatted(Number(currentItem.x) + Number(currentItem.width)),
                width: widthOnImg,
                realWidth: CustomDecimals.toNumberFormatted(this.getProportionalRealSizeValue(widthOnImg), 0)
              })
            }
          }
        }
      }

      if (this.hasRightGable) {
        let rightGableX = this.getProportionalXValue(this.wardrobeWidth - this.data.sideWallThickness);
        dimensionsArray.push({
          x: rightGableX,
          width: this.wardrobeImgSideWallThickness,
          realWidth: this.data.sideWallThickness
        });
      }

      return dimensionsArray;
    },
    inventWallAdditionalDistFromWardrobe () {
      // there always needs to be 10mm space between wall/impact/gable and inventory, on both sides
      return this.getProportionalSizeValue(10);
    },
    horizItemMinSpaceBetweenOnImg () {
      return Number(this.getProportionalSizeValue(this.horizItemMinSpaceBetween));
    },
    inventoryBoardThicknessOnImg () {
      return Number(this.getProportionalSizeValue(this.inventoryBoardThickness));
    },
    inventoryBoardHeightOnImg () {
      return Number(this.getProportionalSizeValue(this.inventoryBoardHeight));
    },
    frontViewVerticalItems () {
      return this.frontViewInventory.filter(item => item.positionType === 'vertical');
    },
    frontViewHorizontalItems () {
      return this.frontViewInventory.filter(item => item.positionType === 'horizontal');
    },
    wardrobeImgDoors () {
      let baseY = this.topViewYOffset + this.wardrobeImgDepth + this.wallImgWidth;
      let baseX = this.wardrobeInnerLeftOffsetX;
      let doorWithOnImg = this.getProportionalSizeValue(this.doorsNumberObject.door_width);
      let doorsOutput = [];
      let doorPartWidth = this.wardrobeImgInnerWidth / this.doorsNumberObject.number;
      let doorPartWidthDiff = doorWithOnImg - doorPartWidth;

      for (let i = 0; i < this.doorsNumberObject.number; i++) {
        let newDoorX = baseX;
        let newDoorY = baseY;
        if (i > 0) {
          newDoorX += ((doorPartWidth * i) - doorPartWidthDiff);
        }
        if ((i % 2) > 0) {
          newDoorY -= 2;
        } else {
          newDoorY -= 10;
        }
        let newDoor = {
          fill: 'black',
          height: 2,
          width: doorWithOnImg,
          x: newDoorX,
          y: newDoorY
        }

        doorsOutput.push(newDoor);
      }

      return doorsOutput
    },
    wardrobeImgDoorsConnectionPoints () {
      let output = [];
      let baseX = this.wardrobeInnerLeftOffsetX;
      let doorPartWidth = this.wardrobeImgInnerWidth / this.doorsNumberObject.number;
      let safeSpaceOnImg = this.getProportionalSizeValue(50);

      for (let i = 1; i < this.doorsNumberObject.number; i++) {
        let newDoorX = baseX;
        newDoorX += doorPartWidth * i;

        output.push({
          x: newDoorX - safeSpaceOnImg,
          width: safeSpaceOnImg * 2
        });
      }

      return output
    },
    widthProportion () {
      return CustomDecimals.toNumberFormatted(Number(this.wardrobeImgHeight) / Number(this.wardrobeHeight), 5);
    },
    allDataLoaded () {
      return this.loaded && this.isResizeComplete;
    },
    includeDoorCollisions () {
      if (this.data && this.doorsNumberObject && this.doorsNumberObject.number && this.doorsNumberObject.number === 4) {
        return false;
      }

      return true;
    },
    selectedItem () {
      if (!this.clickedItemId) {
        return null;
      }

      return this.frontViewInventory.find(ele => ele.id === this.clickedItemId);
    }
  },
  watch: {
    frontViewInventory: {
      handler () {
        if (this.isDragging) {
          return;
        }

        if (!this.includeDoorCollisions) {
          this.existsItemWidthCollision = false
          return;
        }

        this.existsItemWidthCollision = !!(this.frontViewInventory.find(item => item.isInCollision))
      },
      deep: true
    },
    wardrobeAvailableSpaceHoriz (newValue, oldValue) {
      if (!this.sceneInitiated) {
        return;
      }
      let spacesChanged = false;
      if (newValue.length !== oldValue.length) {
        spacesChanged = true;
      } else {
        for (let i = 0; i < newValue.length; i++) {
          if ((newValue[i].x !== oldValue[i].x) || (newValue[i].width !== oldValue[i].width)) {
            spacesChanged = true;
          }
        }
      }

      if (!spacesChanged) {
        return;
      }

      Vue.nextTick(() => {
        this.removeInvalidUnattachedItems();
        let flexShelf = this.inventoryFormatted.find(item => item.width === 'flex' && item.category_id === 3);
        if (!flexShelf) {
          return
        }
        let minWidth = this.getProportionalSizeValue(flexShelf.min_width);
        let maxWidth = this.getProportionalSizeValue(flexShelf.max_width);

        for (let j = 0; j < (newValue.length - 1); j++) {
          let currentFlexTop = this.frontViewInventory.find(item => item.x === newValue[j].x && item.isAdditionalTop);

          if (!currentFlexTop && newValue[j].width <= maxWidth && newValue[j].width >= minWidth) {
            // Add additional tops between gables
            this.addItem({
              itemID: flexShelf.id,
              width: newValue[j].width,
              posX: newValue[j].x,
              isAdditionalTop: true
            });
          }
        }
      });
    },
    '$store.state.wardrobe.price': function(newVal) {
      this.wardrobePrice = newVal;
    }
  },
  data () {
    return {
      canvasHeight: 840,
      canvasWidth: 1010,
      clickedItemId: 0,
      data: {
        impactDepth: 120, // 12cm, TODO: wartość będzie pobierana z API
        sideWallThickness: 12 // 1.2cm, TODO wartość będzie pobierana z API
      },
      dragItemMouseOffset: {
        x: 0,
        y: 0
      },
      dragSourceID: '',
      existsItemWidthCollision: false,
      frontViewInventory: [],
      highlightItemFront: {
        height: 0,
        sourceItemId: '',
        strokeWidth: 4,
        width: 0,
        x: 0,
        y: 0
      },
      highlightItemTop: {
        height: 0,
        isVisible: false,
        sourceItemId: '',
        strokeWidth: 4,
        width: 0,
        x: 0,
        y: 0
      },
      horizItemMinSpaceBetween: 64,
      inventoryBoardHeight: 19,
      inventoryBoardThickness: 19,
      inventoryInDragHeight: 0,
      inventoryInDragId: 0,
      inventoryInDragIsReversed: false,
      inventoryInDragReversedSrc: '',
      inventoryInDragSrc: '',
      inventoryInDragWidth: 0,
      inventoryInDragX: 0,
      isDrag: false,
      isDragging: false,
      isInsertable: false,
      isResizeComplete: false,
      isStepChangeInProgress: false,
      itemsRemovedOnStateLoad: [],
      loaded: false,
      resizeInProgress: false,
      resizeTime: null,
      resizeTimeDelta: 500,
      resizeTimeout: false,
      sceneInitiated: false,
      selectedInventory: [],
      selectedSourceItem: null,
      topViewInventory: [],
      topViewYOffset: 5,
      wallImgWidth: 12,
      wardrobeImgHeight: 0,
      wardrobeImgWidth: 0,
      wardrobePrice: {},
      xOffset: 40,
      yOffset: 40,
      inventoryColors
    }
  },
  mounted () {
    window.addEventListener('resize', this.windowResize, false);
    window.addEventListener('keydown', this.keyAction);

    let maxElementHeight = Math.max(...this.optionsInventory.filter(item => item.height).map(item => item.height));

    if (maxElementHeight) {
      this.inventoryBoardHeight = maxElementHeight;
    }

    this.highlightItemFront.fill = this.inventoryColors.highlightFillColor;
    this.highlightItemFront.stroke = this.inventoryColors.highlightStrokeColor;
    this.highlightItemTop.fill = this.inventoryColors.highlightFillColor;
    this.highlightItemTop.stroke = this.inventoryColors.highlightStrokeColor;

    Vue.nextTick(() => {
      this.loaded = true;

      this.initScene();
    });
  },
  methods: {
    exportToPreset () {
      const transformedData = InventoryParser.parseInventory(this.$store.getters['creator/inventory']);
      console.log('transformedData', transformedData);
    },
    emitOpenPopup (e, inventoryCategory) {
      let inventoryInCategory = this.inventoryFormatted.filter(item => item.category_id === inventoryCategory.id);
      this.$bus.$emit('inventory-selection-popup-show', inventoryCategory.name, inventoryInCategory);
    },
    getIsCategoryInsertable (inventoryCategory) {
      if (!inventoryCategory.snap_to_category || !inventoryCategory.snap_to_category.length) {
        return true;
      }

      let snapToCategoriesOnScene = inventoryCategory.snap_to_category.some(item => this.categoryTypesInScene.indexOf(item) > -1)

      if (inventoryCategory.type !== 'horizontal') {
        return snapToCategoriesOnScene;
      }

      if (snapToCategoriesOnScene) {
        return snapToCategoriesOnScene;
      }

      let inventoryInCategory = this.inventoryFormatted.filter(item => item.category_id === inventoryCategory.id);
      let flexItem = inventoryInCategory.find(item => item.width === 'flex');
      if (!flexItem) {
        return snapToCategoriesOnScene;
      }

      // check if there are at leas 2 walls
      let wallsInWardrobe = this.selectedInventory.filter(item => item === 2);
      return !!(wallsInWardrobe && wallsInWardrobe.length > 1);
    },
    getProportionalSizeValue (value) {
      return CustomDecimals.toNumberFormatted(value * this.widthProportion);
    },
    getProportionalRealSizeValue (value, roundTo10, decimals = 2) {
      let newValue = value / this.widthProportion;
      if (roundTo10 && newValue > 9 && newValue < 11) {
        newValue = 10;
      }
      return CustomDecimals.toNumberFormatted(newValue, decimals);
    },
    getProportionalRealYValue (posYOnScene) {
      let value = this.wardrobeFloorPosY - posYOnScene;
      let newValue = value / this.widthProportion;
      return CustomDecimals.toNumberFormatted(newValue);
    },
    getProportionalYValue (posYReal) {
      let newValue = posYReal * this.widthProportion;
      return CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - newValue);
    },
    getProportionalRealXValue (posXOnScene) {
      let value = posXOnScene - this.xOffset - this.wallImgWidth;
      return CustomDecimals.toNumberFormatted(this.getProportionalRealSizeValue(value), 0);
    },
    getProportionalXValue (posXReal) {
      let newValue = this.getProportionalSizeValue(posXReal);
      return CustomDecimals.toNumberFormatted(newValue + this.xOffset + this.wallImgWidth);
    },
    getNewItemId (inventoryItem) {
      let categoryName = this.optionsInventoryCategories.find(category => category.id === inventoryItem.category_id).name;
      let textId = `item-${inventoryItem.type}-${categoryName}-${inventoryItem.width}-${CustomDecimals.generateUUID()}`;
      return textId.replaceAll(' ', '-');
    },
    addInventory (itemID, categoryType) {
      if (categoryType === 'preset') {
        this.addPreset(itemID);
        return;
      }

      this.addItem({ itemID });
    },
    addItem (options = {}) {
      const {
        itemID,
        width = null,
        posX = null,
        posY = null,
        parentGable = null,
        isGableTop = false,
        isAdditionalTop = false,
        isWardrobeFrame = false,
        itemFromStore = null
      } = options;

      let inventoryItem = this.findAndValidateInventoryItem(itemID, itemFromStore);
      if (!inventoryItem) {
        return null;
      }

      let newItemID = itemFromStore ? itemFromStore.stageID : this.getNewItemId(inventoryItem);

      const positionData = this.calculateItemPosition(inventoryItem, newItemID, {
        width, posX, posY, parentGable, isGableTop, itemFromStore, isWardrobeFrame
      });

      if (!positionData) {
        this.showNoSpaceAlert();
        return null;
      }

      const staticCheck = this.handleFlexToStaticConversion(inventoryItem, positionData.widthOnImg, itemFromStore);
      if (staticCheck) {
        inventoryItem = staticCheck.item;
        newItemID = staticCheck.newID;
        positionData.widthOnImg = staticCheck.width;
      }

      const visualData = this.prepareVisualData(inventoryItem, itemFromStore);

      const newItem = this.createVisualItemObject({
        id: newItemID,
        inventoryItem,
        positionData,
        visualData,
        isAdditionalTop: isAdditionalTop || (itemFromStore && itemFromStore.isAdditionalTop),
        isGableTop: isGableTop || (itemFromStore && itemFromStore.isGableTop),
        attachedItems: itemFromStore ? itemFromStore.attachedItems : []
      });

      if (parentGable && !parentGable.attachedItems.includes(newItemID)) {
        this.updateAttachedItems(parentGable, newItemID);
      }

      this.frontViewInventory.push(newItem);
      this.selectedInventory.push(inventoryItem.id);

      if (!itemFromStore) {
        this.syncItemToStore(newItem, inventoryItem, positionData.widthOnImg);
      }

      if (newItem.inventoryCategoryId === CATEGORY_GABLE) {
        // perform for gables only
        // add gable top shelf
        this.addGableTopShelf(newItem, inventoryItem);
      }

      if (inventoryItem.isPullOut) {
        this.setIfItemHasCollision(newItem);
        this.updateTopViewItemInCollision();
      }

      if (inventoryItem.visibleFromTop && !newItem.isAdditionalTop) {
        this.addToTopView(newItem, inventoryItem, positionData.widthOnImg);
      }

      return newItem;
    },
    findAndValidateInventoryItem (itemID, itemFromStore) {
      let item = this.inventoryFormatted.find(i => i.id === itemID);

      if (itemFromStore && this.itemsRemovedOnStateLoad.includes(itemFromStore.stageID)) {
        return null;
      }

      if (!item) {
        if (itemFromStore) {
          // Cleanup logic dla itemu ze sklepu, który nie istnieje w definicjach
          if (itemFromStore.attachedItems) {
            itemFromStore.attachedItems.forEach(childID => {
              this.$store.commit('creator/removeInventoryAttachedItem', { stageID: itemFromStore.stageID, childID });
              this.itemsRemovedOnStateLoad.push(childID);
            });
          }
          this.itemsRemovedOnStateLoad.push(itemFromStore.stageID);
          this.$store.commit('creator/removeInventoryItem', itemFromStore.stageID);
        }
        return null;
      }

      if (itemFromStore && itemFromStore.isGableTop) {
        this.$store.commit('creator/removeInventoryItem', itemFromStore.stageID);
        return null;
      }

      return item;
    },
    calculateItemPosition (inventoryItem, newItemID, {
      itemFromStore = null,
      posX = null,
      posY = null,
      width = null,
      parentGable = null,
      isGableTop = false,
      isWardrobeFrame = false
    } = {}) {
      let calculatedX = posX;
      let calculatedY = posY;
      let calculatedWidth = inventoryItem.widthOnImg;
      let calculatedIsNotAttached = false;
      let calculatedIsFrame = isWardrobeFrame;

      if (itemFromStore) {
        calculatedX = this.getProportionalXValue(itemFromStore.posX);
        calculatedIsNotAttached = itemFromStore.isNotAttached;
        calculatedIsFrame = itemFromStore.isWardrobeFrame;

        if (inventoryItem.type === 'vertical') {
          calculatedY = CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - inventoryItem.heightOnImg);
        } else {
          calculatedY = this.getProportionalYValue(itemFromStore.posY);
        }

        if (!calculatedWidth && inventoryItem.width === 'flex') {
          calculatedWidth = this.getProportionalSizeValue(itemFromStore.width);
        }
      } else if (posX && width) {
        calculatedX = posX;
        calculatedY = CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - this.inventoryBoardHeightOnImg);
        calculatedWidth = width;
        calculatedIsNotAttached = true;
      } else if (parentGable) {
        calculatedX = parentGable.x;
        if (!parentGable.isReversed) {
          calculatedX += this.inventoryBoardThicknessOnImg;
        }
        if (inventoryItem.width === 'flex') {
          calculatedWidth = width;
        }
        if (isGableTop) {
          calculatedY = parentGable.y;
        }
      } else {
        calculatedX = 0;
        calculatedY = CustomDecimals.toNumberFormatted(this.wardrobeFloorPosY - inventoryItem.heightOnImg);

        if (!this.selectedInventory.length) {
          if (this.wardrobeAvailableSpaceHoriz && this.wardrobeAvailableSpaceHoriz[0]) {
            calculatedX = this.wardrobeAvailableSpaceHoriz[0].x + (this.wardrobeAvailableSpaceHoriz[0].width / 2);
          }
        } else if (inventoryItem.type === 'horizontal') {
          let newPos = this.getNewHorizItemPosition(inventoryItem, newItemID);
          calculatedX = newPos.x;
          calculatedY = newPos.y;
          calculatedIsNotAttached = newPos.isNotAttached;
          if (inventoryItem.width === 'flex' && newPos.newWidth) {
            calculatedWidth = newPos.newWidth;
          }
        } else {
          calculatedX = this.getNewItemPosX(inventoryItem, newItemID);
        }
      }

      if (!calculatedX || !calculatedY) {
        return null;
      }

      return {
        posX: CustomDecimals.toNumberFormatted(calculatedX),
        posY: calculatedY,
        widthOnImg: calculatedWidth,
        isNotAttached: calculatedIsNotAttached,
        isFrame: calculatedIsFrame
      };
    },
    handleFlexToStaticConversion (inventoryItem, currentWidthOnImg, itemFromStore) {
      // this is special case when we takie different precision
      // TODO: sprawdzić dokładność
      if (inventoryItem.width !== 'flex' || itemFromStore || !currentWidthOnImg) {
        return null;
      }

      let flexItemRealWidth = this.getProportionalRealSizeValue(currentWidthOnImg, false, 0);

      let staticEquivalent = this.inventoryFormatted.find(item => item.category_id === inventoryItem.category_id && item.width === flexItemRealWidth);

      if (!staticEquivalent) {
        return null;
      }

      const newItem = { ...staticEquivalent };
      const newID = this.getNewItemId(newItem);
      return {
        item: newItem,
        newID,
        width: newItem.widthOnImg
      };
    },
    prepareVisualData (inventoryItem, itemFromStore) {
      let isReversed = !!this.inventoryInDragIsReversed;
      if (itemFromStore) {
        isReversed = itemFromStore.isReversed;
      }

      let img = new window.Image();
      const src = (inventoryItem.front_reversed && isReversed)
        ? inventoryItem.front_reversed
        : inventoryItem.front;
      img.src = src;

      return { isReversed, img, src };
    },
    createVisualItemObject ({
      id, inventoryItem, positionData, visualData, isAdditionalTop, isGableTop, attachedItems
    }) {
      const newItem = {
        id,
        attachedItems: attachedItems || [],
        height: inventoryItem.heightOnImg,
        image: visualData.img,
        inventoryCategoryId: inventoryItem.category_id,
        inventoryItemId: inventoryItem.id,
        isNotAttached: positionData.isNotAttached,
        isReversed: visualData.isReversed,
        isPullOut: inventoryItem.isPullOut,
        src: visualData.src,
        positionType: inventoryItem.type,
        width: positionData.widthOnImg,
        x: positionData.posX,
        y: positionData.posY,
        lastValidX: positionData.posX,
        lastValidY: positionData.posY,
        opacity: 1,
        stroke: this.inventoryColors.highlightStrokeColor,
        strokeWidth: 0,
        fill: '',
        isWardrobeFrame: positionData.isFrame,
        isFlexWidth: inventoryItem.width === 'flex'
      };

      if (isAdditionalTop) {
        newItem.isAdditionalTop = true;
        newItem.depthOnImg = inventoryItem.depthOnImg;
      }
      if (isGableTop) {
        newItem.isGableTop = true;
      }

      return newItem;
    },
    syncItemToStore (newItem, inventoryItem, widthOnImg) {
      const attachedItemsCopy = [...newItem.attachedItems];
      const itemForStore = { ...inventoryItem };

      if (inventoryItem.width === 'flex' && widthOnImg) {
        itemForStore.isFlexWidth = true;
        itemForStore.width = this.getProportionalRealSizeValue(widthOnImg, false, 2);
      }

      Object.assign(itemForStore, {
        stageID: newItem.id,
        attachedItems: attachedItemsCopy,
        isReversed: newItem.isReversed,
        isNotAttached: newItem.isNotAttached,
        posX: this.getProportionalRealXValue(newItem.x),
        posY: this.getProportionalRealYValue(newItem.y),
        isWardrobeFrame: newItem.isWardrobeFrame
      });

      if (newItem.isAdditionalTop) {
        itemForStore.isAdditionalTop = true;
      }
      if (newItem.isGableTop) {
        itemForStore.isGableTop = true;
      }

      this.$store.commit('creator/addInventoryItem', itemForStore);

      // if (!isAdditionalTop && !parentGable) {
      //   setTimeout(() => {
      //     this.selectItem(newItemID, inventoryItem.category_id);
      //   }, 250);
      // }
    },
    addGableTopShelf (parentGableItem, inventoryItem) {
      const gableTopShelf = this.inventoryFormatted.find(item => item.category_id === CATEGORY_SHELF && item.fitsIn.indexOf(inventoryItem.id) > -1);

      if (gableTopShelf) {
        this.addItem({
          itemID: gableTopShelf.id,
          parentGable: parentGableItem,
          isGableTop: true
        });
      }
    },
    addToTopView (newItem, inventoryItem, widthOnImg) {
      const fillColor = inventoryItem.category_id === CATEGORY_GABLE
        ? this.inventoryColors.inventoryBoxFillColor
        : this.inventoryColors.inventoryFillColor;

      this.topViewInventory.push({
        id: newItem.id,
        height: inventoryItem.depthOnImg,
        inventoryCategoryId: inventoryItem.category_id,
        inventoryItemId: inventoryItem.id,
        width: widthOnImg,
        x: newItem.x,
        y: this.topViewYOffset + this.wallImgWidth,
        lastValidX: newItem.x,
        lastValidY: this.topViewYOffset + this.wallImgWidth,
        fill: fillColor,
        baseFill: fillColor
      });
    },
    showNoSpaceAlert () {
      Vue.swal({
        title: 'Kan ikke tilføje element',
        text: 'Valgte element kan ikke blive tilføjet. Ingen ledig plads blev fundet',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    },
    addWardrobeFrame () {
      let frameIndvendigGavl = this.inventoryFormatted.find(item => item.id === 2 && item.categoryID === CATEGORY_FLEX_GABLE);
      this.addItem({
        itemID: frameIndvendigGavl.id,
        width: frameIndvendigGavl.widthOnImg,
        posX: this.inventoryMinXPos,
        isWardrobeFrame: true
      });

      let rightFramePosX = (this.inventoryMinXPos + this.wardrobeImgInnerAvailableWidth) - frameIndvendigGavl.widthOnImg;
      this.addItem({
        itemID: frameIndvendigGavl.id,
        width: frameIndvendigGavl.widthOnImg,
        posX: rightFramePosX,
        isWardrobeFrame: true
      });
    },
    addPreset (itemID) {
      let preset = this.findAndValidateInventoryItem(itemID);
      if (!preset || !preset.inventory || !preset.inventory.length) {
        console.error('Invalid preset definition');
        return;
      }

      if (preset.width > this.wardrobeRealBiggestAvailableSpaceHoriz) {
        return;
      }

      // presets contain only one "box" with attached items
      let addedParent = this.addItem({ itemID: preset.inventory[0].inventoryItemId });
      preset.inventory[0].attachedItems.forEach(child => {
        if (!child.isGableTop) {
          // gable tops are stored in presets but they are automatically added when gable is added
          let posYOnCanvas = this.getProportionalYValue(child.posY)
          let widthOnCanvas = null;
          if (child.width) {
            widthOnCanvas = this.getProportionalSizeValue(child.width);
          }
          this.addItem({
            itemID: child.inventoryItemId,
            width: widthOnCanvas,
            posY: posYOnCanvas,
            parentGable: addedParent
          });
        }
      })
    },
    handleItemDragStart (e) {
      // console.log('handleItemDragStart', e);
      Vue.set(this, 'isDragging', true);
      Vue.set(this.highlightItemFront, 'height', e.target.attrs.height);
      Vue.set(this.highlightItemFront, 'width', e.target.attrs.width);
      Vue.set(this.highlightItemFront, 'fill', this.inventoryColors.highlightFillColor);
      Vue.set(this.highlightItemFront, 'stroke', this.inventoryColors.highlightStrokeColor);

      let frontViewItem = this.frontViewInventory.find(ele => ele.id === e.target.attrs.id);
      this.selectItem(frontViewItem.id, frontViewItem.inventoryCategoryId);

      let sourceItem = this.inventoryFormatted.find(ele => ele.id === frontViewItem.inventoryItemId);
      if (sourceItem && sourceItem.visibleFromTop) {
        Vue.set(this.highlightItemTop, 'height', sourceItem.depthOnImg);
        Vue.set(this.highlightItemTop, 'width', e.target.attrs.width);
        Vue.set(this.highlightItemTop, 'y', this.topViewYOffset + this.wallImgWidth);
        Vue.set(this.highlightItemTop, 'fill', this.inventoryColors.highlightFillColor);
        Vue.set(this.highlightItemTop, 'stroke', this.inventoryColors.highlightStrokeColor);
        Vue.set(this.highlightItemTop, 'isVisible', true);
      } else {
        Vue.set(this.highlightItemTop, 'isVisible', false);
      }

      let allItemsWidth = frontViewItem.width;
      let allItemsX = frontViewItem.x;

      if (frontViewItem.inventoryCategoryId === 2 && frontViewItem.attachedItems.length) {
        let allHorizontalChildren = this.frontViewInventory.filter(ele => frontViewItem.attachedItems.indexOf(ele.id) > -1 && ele.positionType === 'horizontal');

        for (let i = 0; i < allHorizontalChildren.length; i++) {
          allHorizontalChildren[i].opacity = 0;
        }
      }

      Vue.set(this.dragItemMouseOffset, 'y', e.evt.offsetY - e.target.attrs.y);

      Vue.set(this, 'inventoryInDragWidth', allItemsWidth);
      Vue.set(this, 'inventoryInDragX', allItemsX);

      frontViewItem.lastValidX = e.target.attrs.x;
      frontViewItem.lastValidY = e.target.attrs.y;
    },
    handleItemDragMove (e) {
      // console.log('handleItemDragMove ', e);
      let element = e.target;
      let sourceItem = this.inventoryFormatted.find(item => item.id === element.attrs.inventoryItemId);

      if (this.checkItemOutOfBound(e.evt.offsetX, e.evt.offsetY, element.attrs.x, element.attrs.width)) {
        Vue.set(this, 'isInsertable', false);

        if (sourceItem.type === 'vertical') {
          this.updateTopView(element.attrs.id, element.attrs.x);
        }
        return;
      }

      Vue.set(this.highlightItemFront, 'x', element.attrs.x);
      Vue.set(this.highlightItemFront, 'y', element.attrs.y);
      Vue.set(this.highlightItemTop, 'x', element.attrs.x);

      let snapXPos = null;
      let snapYPos = null;

      if (sourceItem.type === 'vertical') {
        snapXPos = this.getItemXOnCanvas(e.evt.offsetX, sourceItem, element.attrs.id, this.inventoryInDragWidth);
        snapYPos = this.wardrobeFloorPosY - sourceItem.heightOnImg;
      } else {
        let newPosition = this.getHorizItemPosOnCanvas(e.evt.offsetX, CustomDecimals.toNumberFormatted(e.evt.offsetY - this.dragItemMouseOffset.y), sourceItem, element.attrs.id, element.attrs.height);
        snapXPos = newPosition.x;
        snapYPos = newPosition.y;

        if (sourceItem.width === 'flex' && newPosition.newWidth) {
          Vue.set(this.highlightItemFront, 'width', newPosition.newWidth);
          element.attrs.width = newPosition.newWidth;
        }
      }

      if (!snapXPos || !snapYPos) {
        Vue.set(this, 'isInsertable', false);
      } else {
        Vue.set(this.highlightItemFront, 'x', snapXPos);
        Vue.set(this.highlightItemFront, 'y', snapYPos);
        Vue.set(this.highlightItemTop, 'x', snapXPos);
        Vue.set(this, 'isInsertable', true);

        element.attrs.lastValidX = snapXPos;
        element.attrs.lastValidY = snapYPos;
      }

      if (sourceItem.is_pull_out) {
        let elementWidth = element.attrs.width;
        let connectionPointInCollision = this.wardrobeImgDoorsConnectionPoints.find(item => (item.x >= snapXPos && item.x <= snapXPos + elementWidth) || (item.x + item.width >= snapXPos && item.x + item.width <= snapXPos + elementWidth));

        if (connectionPointInCollision && this.includeDoorCollisions) {
          Vue.set(this.highlightItemFront, 'fill', this.inventoryColors.collisionColor);
          Vue.set(this.highlightItemFront, 'stroke', this.inventoryColors.collisionColor);
        } else {
          Vue.set(this.highlightItemFront, 'fill', this.inventoryColors.highlightFillColor);
          Vue.set(this.highlightItemFront, 'stroke', this.inventoryColors.highlightStrokeColor);
        }
      }

      if (sourceItem.type === 'vertical') {
        this.updateTopView(element.attrs.id, element.attrs.x);
      }
    },
    handleItemDragEnd (e) {
      // console.log('handleItemDragEnd ', e);
      Vue.set(this, 'isDragging', false);

      let sourceItem = this.inventoryFormatted.find(item => item.id === e.target.attrs.inventoryItemId);
      let frontViewItem = this.frontViewInventory.find(ele => ele.id === e.target.attrs.id);

      if (!sourceItem) {
        return;
      }

      if (this.checkItemOutOfBound(e.evt.offsetX, e.evt.offsetY, e.target.attrs.x, e.target.attrs.width)) {
        Vue.set(this, 'isInsertable', false);
        this.removeItemFromScene(frontViewItem.id);
        return
      }

      let goToX = e.target.attrs.x;
      let goToY = e.target.attrs.y;
      let goToWidth = e.target.attrs.width;

      if (sourceItem.type === 'vertical') {
        goToX = this.getItemXOnCanvas(e.evt.offsetX, sourceItem, frontViewItem.id, this.inventoryInDragWidth, true);
        goToY = this.wardrobeFloorPosY - sourceItem.heightOnImg;
      } else {
        let newPosition = this.getHorizItemPosOnCanvas(e.evt.offsetX, CustomDecimals.toNumberFormatted(e.evt.offsetY - this.dragItemMouseOffset.y), sourceItem, frontViewItem.id, frontViewItem.height, true);
        goToX = newPosition.x;
        goToY = newPosition.y;

        if (sourceItem.width === 'flex' && newPosition.newWidth) {
          goToWidth = newPosition.newWidth;
        }
      }

      if (!goToX || !goToY) {
        goToX = e.target.attrs.lastValidX;
        goToY = e.target.attrs.lastValidY;
      }

      Vue.nextTick(() => {
        Vue.set(this, 'inventoryInDragWidth', 0);
        Vue.set(this, 'inventoryInDragX', 0);

        e.target.to({
          duration: 0.1,
          x: goToX,
          y: goToY
        });

        if (sourceItem.width === 'flex' && goToWidth) {
          frontViewItem.width = goToWidth;
        }

        this.updateViews(frontViewItem.id, goToX, goToY, sourceItem);

        Vue.nextTick(() => {
          if (frontViewItem.positionType === 'vertical') {
            let attachedItemsChain = this.getAllVerticalAttachedItemsInChain(frontViewItem);
            this.updateAttachedItemsPosition(frontViewItem, attachedItemsChain.chain);
          }
        })
      })
    },
    getItemXOnCanvas (mouseX, sourceItem, movingItemId, movingItemWidth, updateAttachedItems, itemsToIgnore) {
      let snapXPos = null;

      if (sourceItem.category_id === CATEGORY_FLEX_GABLE) {
        snapXPos = this.getWallPositionX(mouseX, sourceItem, movingItemId, movingItemWidth);
      } else {
        snapXPos = this.getSnapPositionX(mouseX, sourceItem, movingItemId, movingItemWidth, updateAttachedItems, false, itemsToIgnore);
      }

      return snapXPos;
    },
    getHorizItemPosOnCanvas (mouseX, mouseY, sourceItem, movingItemId, movingItemHeight, updateAttachedItems) {
      let snapXPos = null;
      let snapYPos = null;
      let snapObj = this.getSnapHorizItemPositionX(mouseX, sourceItem, movingItemId, updateAttachedItems);
      if (snapObj && 'x' in snapObj) {
        snapXPos = snapObj.x;
      }

      if (!snapXPos) {
        return { x: null, y: null };
      }

      snapXPos = CustomDecimals.toNumberFormatted(snapXPos);

      let itemToFitIn = snapObj.parentItem;

      let itemNewWidth = itemToFitIn.width;
      let posY = mouseY;
      if ('id' in itemToFitIn) {
        itemNewWidth -= this.inventoryBoardThicknessOnImg;
      } else {
        let itemToFitInPosY = this.wardrobeFloorPosY - itemToFitIn.height;
        posY = Math.max(mouseY, itemToFitInPosY);
      }

      if (!itemToFitIn) {
        console.log('nie ma do czego przyczepić');
        return { x: null, y: null };
      }

      snapYPos = this.getSnapHorizItemPositionY(posY, itemToFitIn, movingItemId, movingItemHeight);

      if (snapYPos) {
        snapYPos = CustomDecimals.toNumberFormatted(snapYPos);
      }

      return { x: snapXPos, y: snapYPos, newWidth: CustomDecimals.toNumberFormatted(itemNewWidth) };
    },
    getWallPositionX (mouseX, sourceItem, movingItemId, movingItemWidth) {
      if (mouseX < this.inventoryMinXPos) {
        mouseX = this.inventoryMinXPos;
      } else if ((mouseX + movingItemWidth) >= ((this.inventoryMinXPos + this.wardrobeImgInnerAvailableWidth))) {
        mouseX = (this.inventoryMinXPos + this.wardrobeImgInnerAvailableWidth) - movingItemWidth;
      }

      if (this.checkExistsElementVertical(mouseX, movingItemId, movingItemWidth)) {
        let snapXPos = this.getSnapPositionX(mouseX, sourceItem, movingItemId, movingItemWidth, false, true);

        if (snapXPos) {
          return snapXPos;
        }
        return null;
      }
      return mouseX
    },
    getSnapPositionX (mouseX, sourceItem, movingItemId, movingItemWidth, addItemAsChild, checkAnyVertical = false, itemsToIgnore = null) {
      let itemsInCategory = [];

      if (checkAnyVertical) {
        itemsInCategory = this.frontViewInventory.filter(item => (item.positionType === 'vertical') && (item.id !== movingItemId));
      } else if (itemsToIgnore) {
        itemsInCategory = this.frontViewInventory.filter(item => (sourceItem.snapToCategory.indexOf(item.inventoryCategoryId) > -1) && (itemsToIgnore.indexOf(item.id) < 0));
      } else {
        itemsInCategory = this.frontViewInventory.filter(item => (sourceItem.snapToCategory.indexOf(item.inventoryCategoryId) > -1) && (item.id !== movingItemId));
      }

      let currentFrontViewElement = this.frontViewInventory.find(item => item.id === movingItemId);
      if (!itemsToIgnore && currentFrontViewElement && currentFrontViewElement.attachedItems && currentFrontViewElement.attachedItems.length) {
        itemsInCategory = itemsInCategory.filter(item => currentFrontViewElement.attachedItems.indexOf(item.id) < 0);
      }

      let snapItemAfter = false;
      let snapToItem = null;

      if (itemsInCategory.length) {
        let closestItemX = null;

        for (let i = 0; i < itemsInCategory.length; i++) {
          let closestItemXTmp = itemsInCategory[i].x + itemsInCategory[i].width;
          closestItemXTmp = CustomDecimals.toNumberFormatted(closestItemXTmp);

          if (closestItemX === null || Math.abs(mouseX - closestItemX) > Math.abs(mouseX - closestItemXTmp)) {
            if (!this.checkExistsElementVertical(closestItemXTmp, movingItemId, movingItemWidth, itemsToIgnore)) {
              closestItemX = closestItemXTmp;
              snapItemAfter = (itemsInCategory[i].x < closestItemXTmp);
              snapToItem = itemsInCategory[i];
            }
          }

          closestItemXTmp = itemsInCategory[i].x - movingItemWidth;
          closestItemXTmp = CustomDecimals.toNumberFormatted(closestItemXTmp);

          if (closestItemX === null || Math.abs(mouseX - closestItemX) > Math.abs(mouseX - closestItemXTmp)) {
            if (!this.checkExistsElementVertical(closestItemXTmp, movingItemId, movingItemWidth, itemsToIgnore)) {
              closestItemX = closestItemXTmp;
              snapItemAfter = (itemsInCategory[i].x < closestItemXTmp);
              snapToItem = itemsInCategory[i];
            }
          }
        }

        if (closestItemX === null) {
          return closestItemX;
        }

        if (sourceItem.front_reversed) {
          this.reverseItemImage(sourceItem, movingItemId, snapItemAfter);
        }

        if (addItemAsChild) {
          this.updateAttachedItems(snapToItem, movingItemId);
        }

        return closestItemX;
      }

      return null;
    },
    getSnapHorizItemPositionX (mouseX, sourceItem, itemIdOnCanvas, addItemAsChild) {
      let itemsInCategoryIDs = this.inventoryFormatted.filter(item => (sourceItem.fitsIn.indexOf(item.id) > -1)).map(item => item.id);
      let itemsInCategory = this.frontViewInventory.filter(item => (itemsInCategoryIDs.indexOf(item.inventoryItemId) > -1));

      if (sourceItem.width === 'flex') {
        let availableSpacesForItem = this.potentialSpacesForInventoryHoriz.filter(item => item.realWidth >= sourceItem.min_width && item.realWidth <= sourceItem.max_width && item.height);

        itemsInCategory = itemsInCategory.concat(availableSpacesForItem);
      } else {
        let availableSpacesForItem = this.potentialSpacesForInventoryHoriz.filter(item => item.realWidth === sourceItem.width && item.height);

        itemsInCategory = itemsInCategory.concat(availableSpacesForItem);
      }

      if (itemsInCategory.length) {
        let closestItemX = null;
        let snapToItem = null;

        for (let i = 0; i < itemsInCategory.length; i++) {
          if (i === 0) {
            closestItemX = itemsInCategory[i].x;

            if (itemsInCategory[i].isReversed === false) {
              closestItemX += this.inventoryBoardThicknessOnImg
            }

            snapToItem = itemsInCategory[i];
          } else {
            let closestItemXTmp = itemsInCategory[i].x;

            if (closestItemX === null || Math.abs(mouseX - closestItemX) > Math.abs(mouseX - closestItemXTmp)) {
              closestItemX = closestItemXTmp;

              if (itemsInCategory[i].isReversed === false) {
                closestItemX += this.inventoryBoardThicknessOnImg
              }
              snapToItem = itemsInCategory[i];
            }
          }
        }

        if (addItemAsChild) {
          this.updateAttachedItems(snapToItem, itemIdOnCanvas);
        }
        return { x: closestItemX, parentItem: snapToItem };
      }

      return null;
    },
    getSnapHorizItemPositionY (mouseY, itemToFitIn, movingItemId, movingItemHeight) {
      if ('id' in itemToFitIn && (mouseY < itemToFitIn.y + this.inventoryBoardThicknessOnImg + this.horizItemMinSpaceBetweenOnImg)) {
        mouseY = itemToFitIn.y + this.inventoryBoardThicknessOnImg + this.horizItemMinSpaceBetweenOnImg;
      } else if (!('id' in itemToFitIn) && (mouseY < itemToFitIn.y)) {
        mouseY = itemToFitIn.y
      } else if (mouseY > this.wardrobeFloorPosY - this.horizItemMinSpaceBetweenOnImg - movingItemHeight) {
        mouseY = this.wardrobeFloorPosY - this.horizItemMinSpaceBetweenOnImg - movingItemHeight;
      }

      if (!this.checkExistsElementHorizontal(mouseY, itemToFitIn, movingItemId, movingItemHeight)) {
        return mouseY;
      }

      let itemsToCampare = [];
      if (itemToFitIn.attachedItems) {
        let itemsIdsToCampare = itemToFitIn.attachedItems.filter(ele => ele !== movingItemId);

        if (!itemsIdsToCampare || !itemsIdsToCampare.length) {
          return false;
        }
        itemsToCampare = this.frontViewInventory.filter(ele => (itemsIdsToCampare.indexOf(ele.id) > -1) && (ele.positionType === 'horizontal'));
      } else {
        itemsToCampare = this.frontViewInventory.filter(ele => (ele.x === itemToFitIn.x) && (ele.positionType === 'horizontal'));
      }

      if (!itemsToCampare || !itemsToCampare.length) {
        return null;
      }

      let minDiff = movingItemHeight + this.horizItemMinSpaceBetweenOnImg;
      itemsToCampare.sort((CustomDecimals.sortAscBy('y')));

      if (itemsToCampare.length) {
        let closestItemY = null;

        for (let i = 0; i < itemsToCampare.length; i++) {
          if (i === 0) {
            let closestItemYTmp = itemsToCampare[i].y;

            if (itemsToCampare[i].y < mouseY) {
              closestItemYTmp += itemsToCampare[i].height + this.horizItemMinSpaceBetweenOnImg;
            } else {
              closestItemYTmp -= minDiff;
            }

            closestItemYTmp = CustomDecimals.toNumberFormatted(closestItemYTmp);

            if (!this.checkExistsElementHorizontal(closestItemYTmp, itemToFitIn, movingItemId, movingItemHeight)) {
              closestItemY = closestItemYTmp;
            }
          } else {
            let closestItemYTmp = itemsToCampare[i].y;

            if (itemsToCampare[i].y < mouseY) {
              closestItemYTmp += itemsToCampare[i].height + this.horizItemMinSpaceBetweenOnImg;
            } else {
              closestItemYTmp -= minDiff;
            }

            closestItemYTmp = CustomDecimals.toNumberFormatted(closestItemYTmp);

            if (closestItemY === null || Math.abs(mouseY - closestItemY) > Math.abs(mouseY - closestItemYTmp)) {
              if (!this.checkExistsElementHorizontal(closestItemYTmp, itemToFitIn, movingItemId, movingItemHeight)) {
                closestItemY = closestItemYTmp;
              }
            }
          }
        }

        return closestItemY;
      }
      return null;
    },
    checkExistsElementVertical (itemNewPosX, itemId, itemWidth, itemsToIgnore = null) {
      if ((itemNewPosX < this.inventoryMinXPos) || ((itemNewPosX + itemWidth) >= this.inventoryMinXPos + this.wardrobeImgWidth)) {
        Vue.set(this, 'isInsertable', false);
        return true;
      }

      let itemsToCampare = this.frontViewInventory.filter(ele => (ele.positionType === 'vertical') && (ele.id !== itemId));

      if (itemsToIgnore) {
        itemsToCampare = itemsToCampare.filter(ele => itemsToIgnore.indexOf(ele.id) < 0);
      }

      for (let i = 0; i < itemsToCampare.length; i++) {
        if ((CustomDecimals.toNumberFormatted(itemNewPosX + itemWidth) > CustomDecimals.toNumberFormatted(itemsToCampare[i].x)) && (itemNewPosX < CustomDecimals.toNumberFormatted(itemsToCampare[i].x + itemsToCampare[i].width))) {
          return true;
        }
      }

      return false;
    },
    updateViews (currentItemID, posX, posY = null, parentElement) {
      this.updateFrontView(currentItemID, posX, posY, parentElement);
      this.updateTopView(currentItemID, posX);

      this.updateTopViewItemInCollision();
    },
    updateTopView (currentItemId, posX) {
      if (currentItemId && posX) {
        let correspondingItemIndex = this.topViewInventory.findIndex(item => item.id === currentItemId);

        if (correspondingItemIndex > -1) {
          Vue.set(this.topViewInventory[correspondingItemIndex], 'x', posX);
        }
      } else {
        console.log('niepoprawna wartość w update top view: ', currentItemId, posX);
      }
    },
    updateFrontView (currentItemId, posX, posY = null, parentElement) {
      if (currentItemId) {
        let correspondingItem = this.frontViewInventory.find(item => item.id === currentItemId);
        let correspondingItemIndex = this.frontViewInventory.findIndex(item => item.id === currentItemId);

        if (correspondingItem) {
          Vue.set(this.frontViewInventory[correspondingItemIndex], 'x', posX);

          if (correspondingItem.positionType === 'horizontal' && parentElement && parentElement.isReversed === false) {
            correspondingItem.x += this.inventoryBoardThicknessOnImg;
          }

          this.$store.commit('creator/updateInventoryItemProp', { stageID: currentItemId, propName: 'posX', newPropValue: this.getProportionalRealXValue(correspondingItem.x) });

          if (posY) {
            Vue.set(this.frontViewInventory[correspondingItemIndex], 'y', posY);
            this.$store.commit('creator/updateInventoryItemProp', { stageID: currentItemId, propName: 'posY', newPropValue: this.getProportionalRealYValue(posY) });
          }

          if (correspondingItem.isFlexWidth) {
            this.$store.commit('creator/updateInventoryItemProp', { stageID: currentItemId, propName: 'width', newPropValue: this.getProportionalRealSizeValue(correspondingItem.width) });
          }

          this.setIfItemHasCollision(correspondingItem);
          correspondingItem.opacity = 1;
        }

        if (correspondingItem.inventoryCategoryId === 2 && correspondingItem.attachedItems.length) {
          let allHorizontalChildren = this.frontViewInventory.filter(item => correspondingItem.attachedItems.indexOf(item.id) > -1 && item.positionType === 'horizontal');

          for (let i = 0; i < allHorizontalChildren.length; i++) {
            this.updateViews(allHorizontalChildren[i].id, posX, null, correspondingItem);
          }
        }
      }
    },
    reverseItemImage (sourceItem, itemId, isReversed) {
      let itemIndex = this.frontViewInventory.findIndex(ele => ele.id === itemId.toString());
      let frontViewItem = this.frontViewInventory[itemIndex];

      // to powinno być tymczasowe
      if (!frontViewItem || !('isReversed' in frontViewItem)) {
        if (this.inventoryInDragIsReversed === isReversed) {
          return;
        }
        Vue.set(this, 'inventoryInDragIsReversed', isReversed);
        return;
      }

      if (frontViewItem.isReversed === isReversed) {
        return;
      }

      frontViewItem.isReversed = isReversed;
      let imageSrc = isReversed ? sourceItem.front_reversed : sourceItem.front
      let itemFrontImg = new window.Image();
      itemFrontImg.src = imageSrc;
      frontViewItem.src = imageSrc;
      frontViewItem.image = itemFrontImg;

      this.$store.commit('creator/updateInventoryItemProp', { stageID: itemId, propName: 'isReversed', newPropValue: frontViewItem.isReversed });
    },
    checkItemOutOfBound (mouseX, mouseY, dragItemX, dragItemWidth) {
      if (dragItemX < 0 || (dragItemX + dragItemWidth) > this.canvasWidth) {
        return true;
      }

      if (mouseX < this.itemOutOfBoundsBorders.leftBorder || mouseX > this.itemOutOfBoundsBorders.rightBorder) {
        return true;
      }

      if (mouseY < this.itemOutOfBoundsBorders.topBorder || mouseY > this.itemOutOfBoundsBorders.bottomBorder) {
        return true;
      }

      return false;
    },
    removeItemFromScene (idOnScene) {
      let itemInScene = this.frontViewInventory.find(item => item.id === idOnScene.toString());

      let attachedItemsChain = this.getAllAttachedItemsInChain(itemInScene);

      if (itemInScene) {
        for (let i = 0; i < attachedItemsChain.chain.length; i++) {
          let attachedItemID = attachedItemsChain.chain[i];
          let attachedItem = this.frontViewInventory.find(item => item.id === attachedItemID.toString());

          if (attachedItem) {
            let attachedItemInventoryID = attachedItem.inventoryItemId;
            let attachedItemIndex = this.frontViewInventory.findIndex(item => item.id === attachedItemID.toString());
            this.frontViewInventory.splice(attachedItemIndex, 1);
            this.selectedInventory.splice(this.selectedInventory.indexOf(attachedItemInventoryID), 1);
            this.$store.commit('creator/removeInventoryItem', attachedItemID);

            attachedItemIndex = this.topViewInventory.findIndex(item => item.id === attachedItemID.toString());

            if (attachedItemIndex >= 0) {
              this.topViewInventory.splice(attachedItemIndex, 1);
            }
          }
        }

        let itemIndex = this.frontViewInventory.findIndex(item => item.id === idOnScene.toString());
        this.frontViewInventory.splice(itemIndex, 1);
        this.selectedInventory.splice(this.selectedInventory.indexOf(itemInScene.inventoryItemId), 1);
        this.$store.commit('creator/removeInventoryItem', idOnScene);

        let currentParent = this.frontViewInventory.find(item => item.attachedItems.indexOf(idOnScene) > -1);

        if (currentParent) {
          let indexOfChildID = currentParent.attachedItems.indexOf(idOnScene);
          currentParent.attachedItems.splice(indexOfChildID, 1);
          this.$store.commit('creator/removeInventoryAttachedItem', { stageID: currentParent.id, childID: idOnScene });
        }
      }

      let itemIndex = this.topViewInventory.findIndex(item => item.id === idOnScene.toString());

      if (itemIndex >= 0) {
        this.topViewInventory.splice(itemIndex, 1);
      }

      this.updateTopViewItemInCollision();
    },
    removeInvalidUnattachedItems () {
      let unattachedItems = this.frontViewHorizontalItems.filter(item => item.isNotAttached === true);
      for (let i = 0; i < unattachedItems.length; i++) {
        let gapForItem = this.wardrobeAvailableSpaceHoriz.find(item => item.x === unattachedItems[i].x && item.width === unattachedItems[i].width);

        if (!gapForItem) {
          this.removeItemFromScene(unattachedItems[i].id);
        }
      }
    },
    checkExistsElementHorizontal (itemNewPosY, itemToFitIn, movingItemId, movingItemHeight) {
      let isAboveItemToFitIn = false;
      if ('id' in itemToFitIn) {
        isAboveItemToFitIn = !!(itemNewPosY < itemToFitIn.y + this.inventoryBoardThicknessOnImg + this.horizItemMinSpaceBetweenOnImg);
      } else {
        isAboveItemToFitIn = !!(itemNewPosY < itemToFitIn.y);
      }
      let isBelowItemToFitIn = !!(itemNewPosY > this.wardrobeFloorPosY - this.horizItemMinSpaceBetweenOnImg - movingItemHeight);
      if (isAboveItemToFitIn || isBelowItemToFitIn) {
        Vue.set(this, 'isInsertable', false);
        return true;
      }

      let itemsToCampare = [];

      if (itemToFitIn.attachedItems) {
        let itemsIdsToCampare = itemToFitIn.attachedItems.filter(ele => ele !== movingItemId);

        if (!itemsIdsToCampare || !itemsIdsToCampare.length) {
          return false;
        }
        itemsToCampare = this.frontViewInventory.filter(ele => (itemsIdsToCampare.indexOf(ele.id) > -1) && (ele.positionType === 'horizontal'));
      } else {
        itemsToCampare = this.frontViewInventory.filter(ele => (ele.x === itemToFitIn.x) && (ele.positionType === 'horizontal'));
      }

      if (!itemsToCampare.length) {
        return false;
      }

      for (let i = 0; i < itemsToCampare.length; i++) {
        let minY = CustomDecimals.toNumberFormatted(itemsToCampare[i].y + itemsToCampare[i].height + this.horizItemMinSpaceBetweenOnImg);
        let maxY = CustomDecimals.toNumberFormatted(itemsToCampare[i].y - this.horizItemMinSpaceBetweenOnImg - movingItemHeight);
        if (itemNewPosY < minY && itemNewPosY > maxY) {
          return true;
        }
      }
      return false;
    },
    getNewItemPosX (item, itemIdOnCanvas) {
      if (!this.selectedInventory.length) {
        return this.wardrobeAvailableSpaceHoriz[0].x + (this.wardrobeAvailableSpaceHoriz[0].width / 2);
      }

      if (item.category_id === CATEGORY_FLEX_GABLE) {
        let freeSpaceClone = JSON.parse(JSON.stringify(this.wardrobeAvailableSpaceHoriz));
        freeSpaceClone.sort((CustomDecimals.sortAscBy('width')));
        let last = freeSpaceClone.pop();
        return last.x + (last.width / 2);
      }

      let posX = null;

      for (let i = 0; i < this.wardrobeAvailableSpaceHoriz.length; i++) {
        if (this.wardrobeAvailableSpaceHoriz[i].width >= item.widthOnImg) {
          posX = this.getSnapPositionX(this.wardrobeAvailableSpaceHoriz[i].x, item, itemIdOnCanvas, item.widthOnImg, true);

          if (posX) {
            return posX;
          }
        }
      }

      return posX;
    },
    updateAttachedItems (newParent, childIdOnCanvas) {
      if (!childIdOnCanvas) {
        console.log('BRAK ID');
        return;
      }

      let currentParent = this.frontViewInventory.find(item => item.attachedItems.indexOf(childIdOnCanvas) > -1);

      if (currentParent && currentParent.id === newParent.id) {
        return;
      }

      if (currentParent) {
        let indexOfChildID = currentParent.attachedItems.indexOf(childIdOnCanvas);
        currentParent.attachedItems.splice(indexOfChildID, 1);
        this.$store.commit('creator/removeInventoryAttachedItem', { stageID: currentParent.id, childID: childIdOnCanvas });
      }

      if (newParent && ('id' in newParent)) {
        newParent.attachedItems.push(childIdOnCanvas);
        this.$store.commit('creator/addInventoryAttachedItem', { stageID: newParent.id, childID: childIdOnCanvas });
      }

      let child = this.frontViewHorizontalItems.find(item => item.id === childIdOnCanvas);
      if (child) {
        if (newParent && 'id' in newParent) {
          child.isNotAttached = false;
          return;
        }

        child.isNotAttached = true;
      }
    },
    getAvailableSpaceInModule (module, horizontalItems) {
      let availableSpaceArray = [];

      horizontalItems.sort((CustomDecimals.sortAscBy('y')));

      let wardrobeTopY = CustomDecimals.toNumberFormatted(module.y + this.inventoryBoardThicknessOnImg);
      let wardrobeBottomY = CustomDecimals.toNumberFormatted(module.y + module.height);

      for (let i = 0; i < horizontalItems.length; i++) {
        if (i === 0) {
          availableSpaceArray.push({
            height: CustomDecimals.toNumberFormatted(Math.abs(wardrobeTopY - horizontalItems[i].y), 2),
            y: wardrobeTopY
          });
        }

        if (i === horizontalItems.length - 1) {
          availableSpaceArray.push({
            height: CustomDecimals.toNumberFormatted(Math.abs((horizontalItems[i].y + horizontalItems[i].height) - wardrobeBottomY), 2),
            y: CustomDecimals.toNumberFormatted(horizontalItems[i].y + horizontalItems[i].height, 2)
          });
        } else {
          availableSpaceArray.push({
            height: CustomDecimals.toNumberFormatted(Math.abs((horizontalItems[i].y + horizontalItems[i].height) - (horizontalItems[i + 1].y)), 2),
            y: CustomDecimals.toNumberFormatted(horizontalItems[i].y + horizontalItems[i].height, 2)
          });
        }
      }

      return availableSpaceArray;
    },
    updateAttachedItemsPosition (item, attachedItemsChain) {
      if (!item.attachedItems.length) {
        return;
      }

      let itemsToIgnore = [...attachedItemsChain];

      for (let i = 0; i < attachedItemsChain.length; i++) {
        setTimeout(() => {
          let wardrobeItem = this.frontViewInventory.find(ele => ele.id === attachedItemsChain[i]);
          let sourceItem = this.inventoryFormatted.find(ele => ele.id === wardrobeItem.inventoryItemId);

          let posX = wardrobeItem.x;
          posX = this.getItemXOnCanvas(posX, sourceItem, wardrobeItem.id, wardrobeItem.width, true, itemsToIgnore);

          if (!posX) {
            console.log('wrong new posX value', wardrobeItem);
          }

          let indexToRemove = itemsToIgnore.indexOf(wardrobeItem.id);
          itemsToIgnore.splice(indexToRemove, 1);

          this.updateViews(wardrobeItem.id, posX, wardrobeItem.y, item);
        }, 100)
      }
    },
    getNewHorizItemPosY (itemToFitIn, item) {
      let attachedItemsHorizontal = [];

      if (!('id' in itemToFitIn) && item.width === 'flex') {
        attachedItemsHorizontal = this.frontViewHorizontalItems.filter(ele => ele.isNotAttached === true && ele.x === itemToFitIn.x);
      } else {
        attachedItemsHorizontal = this.frontViewInventory.filter(ele => itemToFitIn.attachedItems.indexOf(ele.id) > -1 && ele.positionType === 'horizontal');
      }

      if (!attachedItemsHorizontal || !attachedItemsHorizontal.length) {
        return CustomDecimals.toNumberFormatted(Number(itemToFitIn.y) + (Number(itemToFitIn.height) / 2));
      }

      let availableSpace = this.getAvailableSpaceInModule(itemToFitIn, attachedItemsHorizontal);
      availableSpace.sort((CustomDecimals.sortAscBy('height')));
      let minSpaceNeeded = (this.horizItemMinSpaceBetweenOnImg * 2) + item.heightOnImg;
      let last = availableSpace.pop();

      if (last.height < minSpaceNeeded) {
        return null;
      }

      return CustomDecimals.toNumberFormatted(last.y + ((last.height / 2) - (item.heightOnImg / 2)));
    },
    getNewHorizItemPosition (item, itemIdOnCanvas) {
      let newPosX = null;
      let newPosY = null;
      let itemsInCategoryIDs = this.inventoryFormatted.filter(ele => (item.fitsIn.indexOf(ele.id) > -1)).map(ele => ele.id);
      let itemsInCategory = this.frontViewInventory.filter(ele => (itemsInCategoryIDs.indexOf(ele.inventoryItemId) > -1));

      if (item.width === 'flex') {
        let minWidth = this.getProportionalSizeValue(item.min_width);
        let maxWidth = this.getProportionalSizeValue(item.max_width);
        let availableSpacesForItem = this.wardrobeAvailableSpaceHoriz.filter(ele => ele.width >= minWidth && ele.width <= maxWidth && ele.height);
        itemsInCategory = itemsInCategory.concat(availableSpacesForItem);
      }

      if (itemsInCategory.length) {
        for (let i = 0; i < itemsInCategory.length; i++) {
          let isNotAttachedValue = false;
          if (i === 0) {
            let closestItemYTmp = this.getNewHorizItemPosY(itemsInCategory[i], item);
            if (closestItemYTmp) {
              newPosX = itemsInCategory[i].x;
              newPosY = closestItemYTmp;
              let newWidthVal = itemsInCategory[i].width;

              if (!('id' in itemsInCategory[i])) {
                isNotAttachedValue = true;
              } else {
                if (!itemsInCategory[i].isReversed) {
                  newPosX += this.inventoryBoardThicknessOnImg
                }

                newWidthVal = itemsInCategory[i].width - this.inventoryBoardThicknessOnImg
              }

              this.updateAttachedItems(itemsInCategory[i], itemIdOnCanvas);
              return {
                x: newPosX,
                y: newPosY,
                newWidth: newWidthVal,
                isNotAttached: isNotAttachedValue
              };
            }
          } else {
            let closestItemYTmp = this.getNewHorizItemPosY(itemsInCategory[i], item);
            if (closestItemYTmp) {
              newPosX = itemsInCategory[i].x;
              newPosY = closestItemYTmp;
              let newWidthVal = itemsInCategory[i].width;

              if (!('id' in itemsInCategory[i])) {
                isNotAttachedValue = true;
              } else {
                if (!itemsInCategory[i].isReversed) {
                  newPosX += this.inventoryBoardThicknessOnImg
                }

                newWidthVal = itemsInCategory[i].width - this.inventoryBoardThicknessOnImg
              }

              this.updateAttachedItems(itemsInCategory[i], itemIdOnCanvas);
              return {
                x: newPosX,
                y: newPosY,
                newWidth: newWidthVal,
                isNotAttached: isNotAttachedValue
              };
            }
          }
        }
      }

      return { x: null, y: null };
    },
    handleItemClick (e, itemID, inventoryCategoryId) {
      if (this.isSummaryVisible) {
        return;
      }
      this.selectItem(itemID, inventoryCategoryId);
    },
    selectItem (itemID, inventoryCategoryId) {
      this.deselectItem();
      Vue.nextTick(() => {
        let item = this.frontViewInventory.find(ele => ele.id === itemID);
        let itemInTopView = this.topViewInventory.find(ele => ele.id === itemID);
        Vue.set(this, 'clickedItemId', itemID);
        Vue.set(this, 'selectedSourceItem', this.inventoryFormatted.find(ele => ele.id === item.inventoryItemId));

        if (item) {
          if (inventoryCategoryId === 2) {
            item.fill = this.inventoryColors.selectedItemFillColor;
          } else {
            item.strokeWidth = 7;
          }
        }

        if (itemInTopView) {
          itemInTopView.fill = this.inventoryColors.selectedItemFillColor;
        }
      })
    },
    deselectItem () {
      if (this.clickedItemId) {
        let item = this.frontViewInventory.find(ele => ele.id === this.clickedItemId);
        let itemInTopView = this.topViewInventory.find(ele => ele.id === this.clickedItemId);
        if (item) {
          if (!item.isInCollision) {
            item.fill = '';
          }

          item.strokeWidth = 0;
        }

        if (itemInTopView) {
          if (itemInTopView.isInCollision && this.includeDoorCollisions) {
            itemInTopView.fill = this.inventoryColors.collisionColor;
          } else {
            itemInTopView.fill = itemInTopView.baseFill;
          }
        }
        Vue.set(this, 'clickedItemId', 0);
        Vue.set(this, 'selectedSourceItem', null);
      }
    },
    removeItem () {
      this.removeItemFromScene(this.clickedItemId);
      this.deselectItem();
    },
    away () {
      this.deselectItem();
    },
    clearScene () {
      Vue.set(this, 'selectedInventory', []);
      Vue.set(this, 'frontViewInventory', []);
      Vue.set(this, 'topViewInventory', []);
      this.updateTopViewItemInCollision();
      this.$store.commit('creator/resetInventory');

      Vue.nextTick(() => {
        this.addWardrobeFrame();
      })
    },
    keyAction (e) {
      if (!this.clickedItemId) {
        return;
      }
      if (e.code === 'Backspace' || e.code === 'Delete') {
        e.preventDefault();
        e.stopPropagation();
        this.removeItem();
        return;
      }
      if (e.code === 'Escape') {
        this.deselectItem();
      }
    },
    getAllVerticalAttachedItemsInChain (topElement) {
      let allAttachedItemsArray = { chain: [] };
      return this.getVerticalAttachedItemsChain(topElement, allAttachedItemsArray);
    },
    getVerticalAttachedItemsChain(element, array) {
      let onlyVertical = element.attachedItems.filter(ele => ele.includes('vertical'));
      if (!onlyVertical.length) {
        return array;
      }

      array.chain = array.chain.concat(onlyVertical);

      for (let i = 0; i < onlyVertical.length; i++) {
        let childElement = this.frontViewInventory.find(ele => ele.id === onlyVertical[i]);
        this.getVerticalAttachedItemsChain(childElement, array);
      }

      return array;
    },
    getAllAttachedItemsInChain (topElement) {
      let allAttachedItemsArray = { chain: [] };
      return this.getAttachedItemsChain(topElement, allAttachedItemsArray);
    },
    getAttachedItemsChain(element, array) {
      if (!element || !element.attachedItems.length) {
        return array
      }
      let allItems = element.attachedItems;

      array.chain = array.chain.concat(allItems);

      for (let i = 0; i < allItems.length; i++) {
        let childElement = this.frontViewInventory.find(ele => ele.id === allItems[i]);
        this.getAttachedItemsChain(childElement, array);
      }

      return array;
    },
    windowResize () {
      Vue.set(this, 'sceneInitiated', false);
      this.isResizeComplete = false;
      this.resizeTime = new Date();
      this.resizeInProgress = true;

      if (this.resizeTimeout === false) {
        this.resizeTimeout = true;
        setTimeout(this.resizeEnd, this.resizeTimeDelta);
      }
    },
    resizeEnd() {
      if (new Date() - this.resizeTime < this.resizeTimeDelta) {
        setTimeout(this.resizeEnd, this.resizeTimeDelta);
      } else {
        // store last resize to avoid multiple repaints after code changes
        let lastResize = parseInt(document.body.getAttribute('data-last-resize-time'), 10);
        // eslint-disable-next-line
        if (isNaN(lastResize) || (new Date() - lastResize) > 1000) {
          this.resizeTimeout = false;
          this.resizeInProgress = false;
          this.initScene();
          document.body.setAttribute('data-last-resize-time', +new Date());
        }
      }
    },
    async initScene () {
      Vue.set(this, 'sceneInitiated', false);
      this.resizeCanvas();
      await Vue.nextTick();
      this.addExistingItemsToWardrobe();
      this.setIfItemHasCollision();
      this.updateTopViewItemInCollision();
      await Vue.nextTick();
      Vue.set(this, 'sceneInitiated', true);
    },
    resizeCanvas () {
      if (this.$refs['stage-wrapper']) {
        this.canvasWidth = this.$refs['stage-wrapper'].offsetWidth;
        let stageWrapperHeight = this.$refs['stage-wrapper'].offsetHeight;
        this.canvasHeight = Math.max(stageWrapperHeight - 250, 660);

        let wardrobeImgHeightMax = this.canvasHeight - this.yOffset - this.wallImgWidth - 50;
        let wardrobeImgWidthMax = this.canvasWidth - ((2 * this.wallImgWidth) + this.xOffset + 10);

        Vue.nextTick(() => {
          let minProportion = Math.min(Number(wardrobeImgWidthMax) / Number(this.wardrobeWidth), Number(wardrobeImgHeightMax) / Number(this.wardrobeHeight));

          this.wardrobeImgWidth = CustomDecimals.toNumberFormatted(this.wardrobeWidth * minProportion, 2);
          this.wardrobeImgHeight = CustomDecimals.toNumberFormatted(this.wardrobeHeight * minProportion, 2);

          // For debugging
          if (this.wardrobeImgWidth > wardrobeImgWidthMax) {
            console.warn('wardrobe width bigger than max');
          }
          if (this.wardrobeImgHeight > wardrobeImgHeightMax) {
            console.warn('wardrobe height bigger than max');
          }
          if (CustomDecimals.toNumberFormatted(Number(this.wardrobeImgHeight) / Number(this.wardrobeHeight)) !== CustomDecimals.toNumberFormatted(Number(this.wardrobeImgWidth) / Number(this.wardrobeWidth))) {
            console.warn('proportions do not match', CustomDecimals.toNumberFormatted(Number(this.wardrobeImgHeight) / Number(this.wardrobeHeight)), CustomDecimals.toNumberFormatted(Number(this.wardrobeImgWidth) / Number(this.wardrobeWidth)));
          }

          let newMinCanvasHeight = this.wardrobeImgHeight + this.yOffset + this.wallImgWidth + 50;
          if (this.canvasHeight > newMinCanvasHeight) {
            this.canvasHeight = newMinCanvasHeight
          }

          let newMinCanvasWidth = this.wardrobeImgWidth + (2 * this.wallImgWidth) + this.xOffset + 10;
          if (this.canvasWidth > newMinCanvasWidth) {
            this.canvasWidth = newMinCanvasWidth
          }
          this.isResizeComplete = true;
        });
      }
    },
    addExistingItemsToWardrobe () {
      this.frontViewInventory.splice(0, this.frontViewInventory.length);
      this.topViewInventory.splice(0, this.topViewInventory.length);

      let itemsInStoreCopy = JSON.parse(JSON.stringify(this.$store.getters['creator/inventory']));

      if (!itemsInStoreCopy.length) {
        this.addWardrobeFrame();
      } else {
        for (let i = 0; i < itemsInStoreCopy.length; i++) {
          this.addItem({
            itemID: itemsInStoreCopy[i].id,
            itemFromStore: itemsInStoreCopy[i]
          });
        }
      }
    },
    updateTopViewItemInCollision () {
      for (let i = 0; i < this.frontViewInventory.length; i++) {
        let correspondingItem = this.frontViewInventory[i];
        let itemInTopView = this.topViewInventory.find(item => item.id === correspondingItem.id);
        if (itemInTopView && correspondingItem.attachedItems && correspondingItem.attachedItems.length) {
          let hasCollision = false;

          for (let j = 0; j < correspondingItem.attachedItems.length; j++) {
            let correspondingChildItem = this.frontViewInventory.find(item => item.id === correspondingItem.attachedItems[j]);
            if (correspondingChildItem && ('isPullOut' in correspondingChildItem) && correspondingChildItem.isPullOut && correspondingChildItem.isInCollision) {
              hasCollision = true;
              break;
            }
          }

          if (this.includeDoorCollisions) {
            this.markItemHasCollision(itemInTopView, hasCollision);
          }
        } else if (itemInTopView && this.includeDoorCollisions) {
          this.markItemHasCollision(itemInTopView, false);
        }
      }
    },
    markItemHasCollision (itemToMark, hasCollision) {
      itemToMark.isInCollision = hasCollision;
      if (this.clickedItemId === itemToMark.id) {
        itemToMark.fill = this.inventoryColors.selectedItemFillColor;
        return;
      }

      if (hasCollision) {
        itemToMark.fill = this.inventoryColors.collisionColor;
        return;
      }

      itemToMark.fill = itemToMark.baseFill;
    },
    setIfItemHasCollision (itemToCheck = null) {
      if (!this.includeDoorCollisions) {
        return;
      }
      let itemsToCheck = [];
      if (itemToCheck) {
        if (!itemToCheck.isPullOut) {
          return;
        }
        itemsToCheck.push(itemToCheck);
      } else {
        itemsToCheck = this.frontViewInventory.filter(item => item.isPullOut);
      }

      for (let i = 0; i < itemsToCheck.length; i++) {
        let elementWidth = itemsToCheck[i].width;
        let elementPosX = itemsToCheck[i].x;
        let connectionPointInCollision = this.wardrobeImgDoorsConnectionPoints.find(item => (item.x >= elementPosX && item.x <= elementPosX + elementWidth) || (item.x + item.width >= elementPosX && item.x + item.width <= elementPosX + elementWidth));

        if (connectionPointInCollision) {
          itemsToCheck[i].fill = this.inventoryColors.collisionColor;
          itemsToCheck[i].isInCollision = true;
        } else {
          itemsToCheck[i].fill = '';
          itemsToCheck[i].isInCollision = false;
        }
      }
    },
    handleStepChangeStop () {
      Vue.set(this, 'isStepChangeInProgress', false);
    },
    async saveState (shouldMarkStepCompleted = false) {
      if (shouldMarkStepCompleted) {
        this.$store.commit('creator/setStepCompletion', { stepName: 'wardrobeInventory', stepCompleted: true });
      }

      await this.$nextTick();
    },
    async goNextStep () {
      if (this.existsItemWidthCollision) {
        this.$el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        return;
      }
      Vue.set(this, 'isStepChangeInProgress', true);
      await Promise.all([
        this.saveState(true),
        this.saveCanvasImg()
      ]);

      this.handleStepChangeStop();
      if (this.$route.query && this.$route.query.wardrobeSummary) {
        this.$router.push({ name: 'orderSummary' });
        return;
      }
      this.$router.replace({ query: { wardrobeSummary: true } });
    },
    async goBack () {
      Vue.set(this, 'isStepChangeInProgress', true);

      await this.saveState();
      this.handleStepChangeStop();
      if (this.$route.query && this.$route.query.wardrobeSummary) {
        this.$router.replace({ query: null });
        return;
      }

      if (this.hasInventoryOnly) {
        this.$router.push({ name: 'wardrobeDimensions' });
        return;
      }

      this.$router.push({ name: 'wardrobeDoorsMaterial' });
    },
    saveCanvasImg () {
      let stageFronViewRef = this.$refs['stage-front'].getNode();
      let stageTopViewRef = this.$refs['stage-top'].getNode();

      this.$store.commit('creator/setScreenshots', {
        front: stageFronViewRef.toDataURL(),
        top: stageTopViewRef.toDataURL()
      });
    },
    getObjectForHorizDimensions (itemX, itemWidth, itemRealWidth) {
      return {
        x: CustomDecimals.toNumberFormatted(itemX),
        width: CustomDecimals.toNumberFormatted(itemWidth),
        realWidth: CustomDecimals.toNumberFormatted(itemRealWidth)
      }
    },
    getHorizDimensionX (dimension, dimensionIndex) {
      if (dimensionIndex === 0) {
        return dimension.x - 14;
      }

      if (dimensionIndex === this.wardrobeInventoryDimensionsHoriz.length - 1) {
        return dimension.x + dimension.width;
      }

      return (dimension.x + (dimension.width / 2)) - 5;
    }
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.keyAction);
    window.removeEventListener('resize', this.windowResize, false);
  }
};
</script>
